// SMSF UI Integration - Main integration module for SMSF UI components
// Integration with existing calculator interfaces and navigation
// Last updated: September 2025

import React, { useState, useEffect, useRef } from 'react';
import { 
    SMSFConfigurationForm, 
    SMSFFeeCalculatorDisplay
} from './SMSFUIComponents.js';
import SMSFComplianceDashboard from './SMSFComplianceDashboard.js';
import SMSFTutorialGuide from './SMSF_Tutorial_Guide.js';
import SMSF_RATES_CONFIG from './SMSF_Rates_Config.js';
import { FinancialConfig } from './FinancialConfig.js';
import { SMSFAdministrationModule } from './SMSFAdministrationModule.js';
import SMSFEnhancedFeatures from './components/SMSFEnhancedFeatures.js';
import SMSFDataManager from './SMSFDataManager.js';
import SMSFInvestmentStrategyBuilder from './SMSFInvestmentStrategyBuilder.js';
import InvestmentStrategyBuilder from './components/InvestmentStrategyBuilder.js';
import { trackLeadCaptureEvent, postCalculatorLeadCapture } from './leadCaptureAnalytics.js';
import SMSFEnhancedFeeAnalysis from './SMSFEnhancedFeeAnalysis.js';
import SMSFTutorialSystem from './SMSFTutorialSystem.js';
import DraggableTutorialPanel from './components/DraggableTutorialPanel.js';
import SMSFAssetValuationManager from './SMSFAssetValuationManager.js';
import SMSFAssetAllocationModal from './SMSFAssetAllocationModal.js';
import SMSFRiskAssessmentEngine from './SMSFRiskAssessmentEngine.js';
import SMSFEnhancedComplianceDashboard from './SMSFEnhancedComplianceDashboard.js';
import SMSFAdministrationManager from './SMSFAdministrationManager.js';
import SMSFAdvancedAnalytics from './SMSFAdvancedAnalytics.js';
import SMSFQuickRetirementCheck from './SMSFQuickRetirementCheck.js';
import SMSFInsuranceAnalysis from './SMSFInsuranceAnalysis.js';
import SMSFEstatePlanning from './SMSFEstatePlanning.js';
import SMSFPerformanceOptimization from './SMSFPerformanceOptimization.js';
import SMSFLRBACalculator from './SMSFLRBACalculator.js';
import SMSFDivision293Calculator from './SMSFDivision293Calculator.js';
import SMSFDivision296Calculator from './SMSFDivision296Calculator.js';
import SMSFValidationPanel from './SMSFValidationPanel.js';
import { SMSFStateProvider, useSMSFState } from './SMSFStateContext.js';
import { useLegacySMSFState } from './SMSFStateMigrationWrapper.js';
import SMSFCollectiblesManager from './SMSFCollectiblesManager.js';
import SMSFRelatedPartyMonitor from './SMSFRelatedPartyMonitor.js';
import { alertCheckoutFailure, startRecommendedCheckout } from './utils/recommendedCheckout.js';
import SMSFTaxLossHarvesting from './SMSFTaxLossHarvesting.js';
import SMSFReportingCenter from './SMSFReportingCenter.js';
import { SMSFExportEngine } from './SMSFExportEngine.js';
import SMSFContributionCarryForward from './SMSFContributionCarryForward.js';
import SMSFInvestmentLimitsMonitor from './SMSFInvestmentLimitsMonitor.js';
import SMSFGovernmentCoContribution from './SMSFGovernmentCoContribution.js';
import SMSFDownsizerContribution from './SMSFDownsizerContribution.js';
import SMSFCalculatorWizard from './SMSFCalculatorWizard.js';
import SMSFvsIndustryComparison from './SMSFvsIndustryComparison.js';
import SMSFEmailReporting from './SMSFEmailReporting.js';
import SMSFBringForwardCalculator from './SMSFBringForwardCalculator.js';
import SMSFWorkTestTool from './SMSFWorkTestTool.js';
import SMSFFHSSCalculator from './SMSFFHSSCalculator.js';
import SMSFSpouseContributionSplitting from './SMSFSpouseContributionSplitting.js';
import SMSFUnifiedDashboard from './SMSFUnifiedDashboard.js';
import SMSFSuccessionPlanning from './SMSFSuccessionPlanning.js';
import SMSFMobileWrapper from './components/SMSFMobileWrapper.js';
import SMSFErrorBoundary, { LightweightErrorBoundary } from './components/SMSFErrorBoundary.js';
import SMSFRelatedCalculators from './components/SMSFRelatedCalculators.js';
import SMSFSearchBar from './components/SMSFSearchBar.js';
import SMSFHistoryManager from './SMSFHistoryManager.js';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';
import InfoTooltip from './components/InfoTooltip.js';
import { SubscriptionManager } from './SubscriptionManager.js';
import { EmailCaptureForm } from './components/EmailCaptureForm.js';
import './styles/smsf-mobile.css';



// ============================================================
// SMSFFreeRunsEmailGate: shown when the 1 anonymous SMSF session is used.
// Capturing an email grants 1 more free session.
// ============================================================
function SMSFFreeRunsEmailGate({ onEmailCaptured, onUpgradeClick, onDismiss }) {
    const [email, setEmail] = React.useState('');
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [error, setError] = React.useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setIsSubmitting(true);
        setError(null);

        const trimmed = email.trim();
        const source = 'smsf_free_runs_gate';
        trackLeadCaptureEvent('free_runs_email_capture_attempt', {
            source,
            calculatorTier: 'smsf'
        });

        SubscriptionManager.setSMSFFreeEmail(trimmed);

        try {
            const { ok, data } = await postCalculatorLeadCapture({
                email: trimmed,
                calculatorType: 'smsf',
                source
            });
            const leadStored = ok && data.leadStored !== false;
            if (leadStored) {
                trackLeadCaptureEvent('free_runs_email_captured', {
                    source,
                    calculatorTier: 'smsf',
                    leadStored: true
                });
            } else {
                trackLeadCaptureEvent('free_runs_email_capture_failed', {
                    source,
                    calculatorTier: 'smsf',
                    leadStored: false,
                    error: data.error || data.message || 'capture_failed'
                });
            }
        } catch (err) {
            trackLeadCaptureEvent('free_runs_email_capture_failed', {
                source,
                calculatorTier: 'smsf',
                leadStored: false,
                error: err?.message || 'network_error'
            });
        }

        setIsSubmitting(false);
        onEmailCaptured(trimmed);
    };

    return (
        <div className="mb-6 rounded-xl border-2 border-orange-200 bg-orange-50 shadow-lg overflow-hidden">
            <div className="bg-orange-500 px-6 py-3 flex items-center justify-between">
                <p className="text-white font-bold text-base">You've used your free SMSF session</p>
                <button type="button" onClick={onDismiss} className="text-white/80 hover:text-white text-xl leading-none">×</button>
            </div>
            <div className="px-6 py-5">
                <p className="text-gray-800 font-medium mb-1">Enter your email to unlock 1 more free session — no payment, no commitment.</p>
                <p className="text-gray-600 text-sm mb-4">We'll also send you an <strong>SMSF Compliance Checklist</strong>. Unsubscribe anytime.</p>
                <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4 text-xs text-blue-700">
                    <strong>Privacy Notice:</strong> We use IP monitoring to ensure fair usage of our free tools. Your email will be stored securely and never shared. See our <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-900">Privacy Policy</a> for details.
                </div>
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 mb-4">
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your.email@example.com"
                        required
                        disabled={isSubmitting}
                        className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 text-base disabled:bg-gray-100"
                    />
                    <button
                        type="submit"
                        disabled={isSubmitting || !email.trim()}
                        className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg transition disabled:opacity-50 text-base whitespace-nowrap"
                    >
                        {isSubmitting ? 'Unlocking…' : 'Unlock 1 more session'}
                    </button>
                </form>
                {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
                <div className="border-t border-orange-200 pt-4">
                    <p className="text-gray-600 text-sm mb-2">Or skip straight to unlimited access:</p>
                    <button
                        type="button"
                        onClick={onUpgradeClick}
                        className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition text-sm"
                    >
                        Upgrade to All Access — $39.99/month
                    </button>
                </div>
            </div>
        </div>
    );
}

// Internal component that uses context state
const SMSFCalculatorInterfaceInternal = ({ onDataChange }) => {
    // Ref for auto-scrolling to content area when tab changes
    const contentRef = React.useRef(null);
    // LOCALHOST AUTO-BYPASS check (used throughout)
    const isLocalhost = typeof window !== 'undefined' && 
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') &&
        !window.location.hostname.includes('netlify') &&
        !window.location.hostname.includes('supercalcpro');

    // Free run state — track SMSF suite free trial usage
    const [smsfFreeRunsStatus, setSmsfFreeRunsStatus] = React.useState(() => 
        SubscriptionManager.canRunSMSFFree()
    );
    const [showSmsfEmailGate, setShowSmsfEmailGate] = React.useState(false);
    const [showSmsfPostUpsell, setShowSmsfPostUpsell] = React.useState(false);
    const smsfRunRecordedRef = React.useRef(false); // prevents double-counting within a session

    // Demo mode: only when free runs are exhausted (or on production with no subscription)
    const [isDemoMode, setIsDemoMode] = React.useState(() => {
        if (isLocalhost) return false;
        if (SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SMSF_SUITE)) return false;
        return !SubscriptionManager.canRunSMSFFree().canRun;
    });

    // Only force demo mode when free runs are exhausted (not on first visit)
    React.useEffect(() => {
        if (isLocalhost) {
            setIsDemoMode(false);
            return;
        }
        const hasSubscription = SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SMSF_SUITE);
        if (!hasSubscription && !SubscriptionManager.canRunSMSFFree().canRun) {
            setIsDemoMode(true);
        }
    }, []);

    // Check if inputs should be locked
    const hasSMSFAccess = SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SMSF_SUITE);
    const inputsLocked = isLocalhost ? false : (isDemoMode || (!hasSMSFAccess && !SubscriptionManager.canRunSMSFFree().canRun));

    const applySmsfQuickStart = (preset) => {
        const presets = {
            setup: 'configuration',
            contribution: 'carry_forward',
            pension: 'compliance'
        };
        const targetTab = presets[preset];
        if (!targetTab) return;
        setActiveTab(targetTab);

        if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('event', 'preset_selected', {
                event_category: 'Calculator Usage',
                event_label: `smsf_${preset}`,
                calculator_tier: 'smsf'
            });
        }
    };

    // Use migration wrapper to access context state with old API
    const {
        activeTab,
        setActiveTab,
        showTutorial,
        setShowTutorial,
        smsfData,
        setSmsfData,
        memberProfile,
        setMemberProfile,
        constraints,
        setConstraints,
        smsfTrusteeCount,
        setSmsfTrusteeCount,
        smsfEstablishedDate,
        setSmsfEstablishedDate,
        smsfAuditFees,
        setSmsfAuditFees,
        smsfAdminFees,
        setSmsfAdminFees,
        smsfAccountingFees,
        setSmsfAccountingFees,
        smsfConcessionalUsed,
        setSmsfConcessionalUsed,
        smsfNonConcessionalUsed,
        setSmsfNonConcessionalUsed,
        yourTbcUsed,
        setYourTbcUsed,
        partnerTbcUsed,
        setPartnerTbcUsed,
        smsfDirectProperty,
        setSmsfDirectProperty,
        smsfPropertyValue,
        setSmsfPropertyValue,
        smsfPropertyLoan,
        setSmsfPropertyLoan,
        smsfPropertyInterestRate,
        setSmsfPropertyInterestRate,
        smsfPropertyLoanTerm,
        setSmsfPropertyLoanTerm,
        smsfPropertyRentalYield,
        setSmsfPropertyRentalYield,
        smsfInHouseAssets,
        setSmsfInHouseAssets,
        smsfRelatedPartyLoans,
        setSmsfRelatedPartyLoans,
        smsfCommercialProperty,
        setSmsfCommercialProperty,
        deathBenefitNominations,
        setDeathBenefitNominations,
        reversionaryPensionEnabled,
        setReversionaryPensionEnabled,
        reversionaryPensionAmount,
        setReversionaryPensionAmount,
        spouseAge,
        setSpouseAge,
        beneficiaries,
        setBeneficiaries,
        taxFreeComponent,
        setTaxFreeComponent,
        taxableComponent,
        setTaxableComponent,
        assetAllocationTargets,
        setAssetAllocationTargets,
        assetAllocationActual,
        setAssetAllocationActual,
        assetValues,
        setAssetValues,
        assetPerformance,
        setAssetPerformance
    } = useLegacySMSFState();

    // Access full SMSF state for alternative assets transfer
    const smsfState = useSMSFState();

    // Asset Valuation Manager computed state
    const [assetValuationData, setAssetValuationData] = useState({
        assetValues: {},
        productiveAssets: {},
        assetDetails: null,
        residentialPropertyValue: 0,
        residentialRentalIncome: 0,
        residentialPropertyRentalYield: 0,
        residentialPropertyCapitalGrowth: 0,
        commercialPropertyValue: 0,
        commercialRentalIncome: 0,
        commercialPropertyRentalYield: 0,
        commercialPropertyCapitalGrowth: 0,
        totalLoanAmount: 0,
        avgInterestRate: 0,
        avgLoanTerm: 0
    });

    // Asset Allocation Modal state
    const [showAllocationModal, setShowAllocationModal] = useState(false);
    const [assetBreakdownForAllocation, setAssetBreakdownForAllocation] = useState({});

    // Selected member for transfer
    const [selectedMemberId, setSelectedMemberId] = useState(null);

    // Helper function to calculate age from DOB
    const calculateAge = (dob) => {
        if (!dob) return null;
        const birthDate = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    // Record a free SMSF run the first time the user actually enters their fund data.
    // Triggered when totalAssets / fundBalance goes above zero — meaning they've typed real numbers.
    React.useEffect(() => {
        if (isLocalhost) return;
        if (hasSMSFAccess) return;
        if (smsfRunRecordedRef.current) return;

        const fundBalance = smsfData?.totalAssets || smsfData?.balance || smsfData?.fundBalance || 0;
        if (fundBalance > 0) {
            smsfRunRecordedRef.current = true;
            SubscriptionManager.recordSMSFRun();
            const newStatus = SubscriptionManager.canRunSMSFFree();
            setSmsfFreeRunsStatus(newStatus);
            if (!newStatus.canRun) {
                setIsDemoMode(true);
            }
            setShowSmsfPostUpsell(true);
            if (typeof window !== 'undefined' && window.gtag) {
                window.gtag('event', 'smsf_free_run_consumed', {
                    event_category: 'free_trial',
                    event_label: `runs_remaining_${newStatus.runsLeft}`,
                    value: 1,
                    calculator_tier: 'smsf'
                });
            }
        }
    }, [smsfData]);

    // Helper function to derive members array from Configuration data (single source of truth)
    const getDerivedMembers = () => {
        const config = smsfData || {};
        const numberOfMembers = config.numberOfMembers || 0;
        const members = [];

        for (let i = 1; i <= Math.min(numberOfMembers, 6); i++) {
            const prefix = `member${i}`;
            const name = config[`${prefix}Name`];
            const dob = config[`${prefix}DOB`];
            const balance = config[`${prefix}Balance`] || 0;
            
            // Only add member if they have a name
            if (name && name.trim()) {
                members.push({
                    name: name.trim(),
                    age: calculateAge(dob) || null,
                    dob: dob,
                    tfn: config[`${prefix}TFN`],
                    address: config[`${prefix}Address`],
                    memberType: config[`${prefix}Type`] || 'accumulation',
                    balance: balance,
                    tbcUsed: config[`${prefix}TBCUsed`] || 0,
                    concessionalUsed: config[`${prefix}ConcessionalUsed`] || 0,
                    nonConcessionalUsed: config[`${prefix}NonConcessionalUsed`] || 0,
                    retirementAge: 67, // Default retirement age
                    // Preserve any existing asset allocation from modal
                    assetAllocation: (smsfData?.members || []).find(m => m.name === name.trim())?.assetAllocation
                });
            }
        }

        return members;
    };

    // Helper function to calculate member's proportional asset allocation
    const getMemberAssetAllocation = () => {
        const members = getDerivedMembers(); // Use derived members from Configuration
        if (members.length === 0) return null;

        // Get selected member or default to first member
        const selectedMember = selectedMemberId 
            ? members.find(m => m.name === selectedMemberId) || members[0]
            : members[0];

        // Calculate total fund balance
        const totalFundBalance = members.reduce((sum, m) => sum + (m.balance || 0), 0);
        const memberBalance = selectedMember.balance || 0;
        const memberProportion = totalFundBalance > 0 ? memberBalance / totalFundBalance : 0;

        // Check if member has explicit asset allocation from modal
        if (selectedMember.assetAllocation) {
            const alloc = selectedMember.assetAllocation;
            return {
                member: selectedMember,
                proportion: memberProportion,
                productiveAssets: alloc.productiveAssets || {},
                nonProductiveAssets: alloc.nonProductiveAssets || {},
                hasExplicitAllocation: true
            };
        }

        // Otherwise calculate proportional split from total fund assets
        const sharesValue = assetValuationData?.assetValues?.shares || 0;
        const infrastructureValue = assetValuationData?.assetValues?.alternatives || 0;
        const cashValue = assetValuationData?.assetValues?.cash || 0;
        const commercialPropValue = assetValuationData?.commercialPropertyValue || 0;
        const residentialPropValue = assetValuationData?.residentialPropertyValue || 0;
        const collectiblesValue = assetValuationData?.assetValues?.collectibles || 0;
        const commercialRentalIncome = assetValuationData?.commercialRentalIncome || 0;
        const residentialRentalIncome = assetValuationData?.residentialRentalIncome || 0;

        return {
            member: selectedMember,
            proportion: memberProportion,
            productiveAssets: {
                shares: sharesValue * memberProportion,
                infrastructure: infrastructureValue * memberProportion,
                cash: cashValue * memberProportion
            },
            nonProductiveAssets: {
                commercialProperty: commercialPropValue * memberProportion,
                commercialRentalIncome: commercialRentalIncome * memberProportion,
                residentialProperty: residentialPropValue * memberProportion,
                residentialRentalIncome: residentialRentalIncome * memberProportion,
                collectibles: collectiblesValue * memberProportion
            },
            hasExplicitAllocation: false
        };
    };

    // Search functionality state
    const [showSearch, setShowSearch] = useState(false);

    // Keyboard shortcut for search (Ctrl+K or Cmd+K)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                setShowSearch(true);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        if (onDataChange) {
            onDataChange(smsfData);
        }
    }, [smsfData, onDataChange]);

    const handleConfigurationChange = (newConfig) => {
        if (inputsLocked) {
            setShowSmsfEmailGate(true);
            // Scroll to top to show the email gate modal
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        // Map fundBalance to both balance (used by validators) and totalAssets (used by dashboards)
        const fundBal = newConfig.fundBalance || newConfig.totalAssets || 0;
        const mappedConfig = {
            ...newConfig,
            balance: fundBal,
            totalAssets: fundBal
        };
        setSmsfData(prev => ({ ...prev, ...mappedConfig }));
    };

    const handleMemberProfileChange = (newProfile) => {
        if (inputsLocked) {
            setShowSmsfEmailGate(true);
            // Scroll to top to show the email gate modal
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        setMemberProfile(newProfile);
    };

    // Categorized tabs for better organization
    const tabCategories = [
        {
            name: 'Overview',
            icon: '📊',
            tabs: [
                { id: 'configuration', label: 'Personal/Fund Details', icon: '👤' },
                { id: 'dashboard', label: 'Dashboard', icon: '📊' },
                { id: 'wizard', label: 'Setup Wizard', icon: '🧙‍♂️' },
                { id: 'comparison', label: 'SMSF vs Industry', icon: '⚖️' }
            ]
        },
        {
            name: 'Contributions',
            icon: '💰',
            tabs: [
                { id: 'carry_forward', label: 'Carry-Forward', icon: '📅' },
                { id: 'bring_forward', label: 'Bring-Forward', icon: '🔄' },
                { id: 'co_contribution', label: 'Gov Co-Contribution', icon: '💚' },
                { id: 'downsizer', label: 'Downsizer', icon: '🏠' },
                { id: 'division293', label: 'Division 293', icon: '💵' },
                { id: 'division296', label: 'Division 296', icon: '🏦' },
                { id: 'work_test', label: 'Work Test', icon: '⏱️' },
                { id: 'fhss', label: 'FHSS', icon: '🏡' },
                { id: 'spouse_split', label: 'Spouse Splitting', icon: '💑' }
            ]
        },
        {
            name: 'Investment & Assets',
            icon: '🏢',
            tabs: [
                { id: 'investment', label: 'Investment Strategy', icon: '📊' },
                { id: 'investment_ato', label: 'Strategy (ATO Compliant)', icon: '⚖️' },
                { id: 'lrba', label: 'LRBA Calculator', icon: '🏛️' },
                { id: 'limits_monitor', label: 'Limits Monitor', icon: '📊' },
                { id: 'collectibles', label: 'Collectibles', icon: '🎨' },
                { id: 'related_party', label: 'Related Party', icon: '👥' },
                { id: 'valuation', label: 'Valuation', icon: '🏠' }
            ]
        },
        {
            name: 'Pensions & Income',
            icon: '💸',
            tabs: [
                { id: 'quick_check', label: 'Quick Check', icon: '⚡' },
                { id: 'tax_harvesting', label: 'Tax Harvesting', icon: '📉' }
            ]
        },
        {
            name: 'Compliance & Admin',
            icon: '✅',
            tabs: [
                { id: 'compliance', label: 'Compliance', icon: '✅' },
                { id: 'enhanced_compliance', label: 'Enhanced', icon: '🛡️' },
                { id: 'administration', label: 'Administration', icon: '📋' }
            ]
        },
        {
            name: 'Planning & Analysis',
            icon: '📈',
            tabs: [
                { id: 'analytics', label: 'Analytics', icon: '📈' },
                { id: 'risk', label: 'Risk Assessment', icon: '⚠️' },
                { id: 'performance', label: 'Performance', icon: '⚡' },
                { id: 'insurance', label: 'Insurance', icon: '🏥' },
                { id: 'estate', label: 'Estate Planning', icon: '📜' },
                { id: 'succession', label: 'Succession', icon: '👨‍👩‍👧‍👦' }
            ]
        },
        {
            name: 'Reporting',
            icon: '📄',
            tabs: [
                { id: 'reports', label: 'Reports', icon: '📄' },
                { id: 'email_reports', label: 'Email', icon: '📧' },
                { id: 'history', label: 'History & Trends', icon: '📈' },
                { id: 'data', label: 'Data', icon: '💾' }
            ]
        },
        {
            name: 'Advanced',
            icon: '🚀',
            tabs: [
                { id: 'strategy', label: 'Strategy Builder', icon: '📊' },
                { id: 'enhanced', label: 'Enhanced Features', icon: '🚀' }
            ]
        }
    ];

    // Flatten tabs for easy lookup
    const tabs = tabCategories.flatMap(cat => cat.tabs);

    // State for mobile category menu
    const [expandedCategory, setExpandedCategory] = useState(null);

    // Find current tab's category
    const getCurrentCategory = () => {
        return tabCategories.find(cat => cat.tabs.some(tab => tab.id === activeTab));
    };

    const renderCategorizedNavigation = () => (
        <div className="mb-6">
            {/* Mobile: Horizontal Scrollable Navigation */}
            <div className="md:hidden">
                {/* Category Pills - Horizontal Scroll with Snap */}
                <div className="overflow-x-auto scrollbar-hide -mx-4 px-4 mb-3">
                    <div className="flex gap-2 pb-2" style={{ scrollSnapType: 'x mandatory' }}>
                        {tabCategories.map(category => {
                            const isActiveCategory = category.tabs.some(tab => tab.id === activeTab);
                            return (
                                <button
                                    key={category.name}
                                    onClick={() => {
                                        const newExpanded = expandedCategory === category.name ? null : category.name;
                                        setExpandedCategory(newExpanded);
                                        if (newExpanded) {
                                            // Auto-select first tab in category
                                            setActiveTab(category.tabs[0].id);
                                        }
                                    }}
                                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full whitespace-nowrap font-medium text-sm transition-all shadow-sm ${
                                        isActiveCategory
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-white text-gray-700 border border-gray-300'
                                    }`}
                                    style={{ scrollSnapAlign: 'start' }}
                                >
                                    <span className="text-base">{category.icon}</span>
                                    <span>{category.name}</span>
                                    {isActiveCategory && (
                                        <svg 
                                            className={`w-4 h-4 transition-transform ${expandedCategory === category.name ? 'rotate-180' : ''}`}
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
                
                {/* Member Selector for Overview - Mobile */}
                {(() => {
                    const derivedMembers = getDerivedMembers();
                    if (expandedCategory !== 'Overview' || derivedMembers.length === 0) return null;
                    
                    const currentMember = selectedMemberId 
                        ? derivedMembers.find(m => m.name === selectedMemberId) || derivedMembers[0]
                        : derivedMembers[0];
                    
                    return (
                        <div className="mb-3 px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-300 shadow-sm">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                    <span className="text-2xl">👤</span>
                                    <div>
                                        <div className="text-xs font-medium text-gray-600 uppercase">Viewing Member</div>
                                        <div className="text-lg font-bold text-blue-900">{currentMember.name}</div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-xs text-gray-600">Age</div>
                                    <div className="text-sm font-semibold text-gray-800">{currentMember.age || 'Not set'}</div>
                                </div>
                            </div>
                            {derivedMembers.length > 1 && (
                                <select
                                    value={selectedMemberId || derivedMembers[0]?.name || ''}
                                    onChange={(e) => setSelectedMemberId(e.target.value)}
                                    className="w-full text-sm px-3 py-2 border-2 border-blue-400 rounded-lg bg-white font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                                >
                                    {derivedMembers.map((member, index) => (
                                        <option key={member.name || `member-${index}`} value={member.name}>
                                            Switch to: {member.name} (Age {member.age || '?'}) - ${(member.balance || 0).toLocaleString()}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    );
                })()}
                
                {/* Tool Tabs within Category - Horizontal Scroll with Snap */}
                {expandedCategory && (
                    <div className="overflow-x-auto scrollbar-hide -mx-4 px-4 mb-4">
                        <div className="flex gap-2 pb-2" style={{ scrollSnapType: 'x mandatory' }}>
                            {tabCategories
                                .find(cat => cat.name === expandedCategory)
                                ?.tabs.map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={() => {
                                            setActiveTab(tab.id);
                                            setTimeout(() => {
                                                if (contentRef.current) {
                                                    const elementPosition = contentRef.current.getBoundingClientRect().top;
                                                    const offsetPosition = elementPosition + window.pageYOffset - 80;
                                                    window.scrollTo({
                                                        top: offsetPosition,
                                                        behavior: 'smooth'
                                                    });
                                                }
                                            }, 150);
                                        }}
                                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg whitespace-nowrap text-sm font-medium transition-all shadow-sm ${
                                            activeTab === tab.id
                                                ? 'bg-blue-100 text-blue-700 border-2 border-blue-400'
                                                : 'bg-white text-gray-700 border border-gray-300'
                                        }`}
                                        style={{ scrollSnapAlign: 'start' }}
                                    >
                                        <span>{tab.icon}</span>
                                        <span>{tab.label}</span>
                                    </button>
                                ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Desktop: Categorized Tabs */}
            <div className="hidden md:block">
                <div className="space-y-4">
                    {tabCategories.map(category => (
                        <div key={category.name}>
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center">
                                    <span className="mr-2 text-lg">{category.icon}</span>
                                    <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                                        {category.name}
                                    </span>
                                </div>
                                
                                {/* Member Selector for Overview */}
                                {(() => {
                                    const derivedMembers = getDerivedMembers();
                                    if (category.name !== 'Overview' || derivedMembers.length === 0) return null;
                                    
                                    const currentMember = selectedMemberId 
                                        ? derivedMembers.find(m => m.name === selectedMemberId) || derivedMembers[0]
                                        : derivedMembers[0];
                                    
                                    return (
                                        <div className="flex items-center space-x-3">
                                            <div className="flex items-center space-x-2 px-3 py-1 bg-blue-100 rounded-lg border border-blue-300">
                                                <span className="text-sm">👤</span>
                                                <span className="text-sm font-bold text-blue-900">{currentMember.name}</span>
                                                <span className="text-xs text-gray-600">({currentMember.age || '?'})</span>
                                            </div>
                                            {derivedMembers.length > 1 && (
                                                <select
                                                    value={selectedMemberId || derivedMembers[0]?.name || ''}
                                                    onChange={(e) => setSelectedMemberId(e.target.value)}
                                                    className="text-xs px-2 py-1 border-2 border-blue-400 rounded bg-white font-medium text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                >
                                                    {derivedMembers.map((member, index) => (
                                                        <option key={member.name || `member-${index}`} value={member.name}>
                                                            ↻ {member.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {category.tabs.map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={() => {
                                            setActiveTab(tab.id);
                                            // Scroll to content area after tab change - more aggressive
                                            setTimeout(() => {
                                                if (contentRef.current) {
                                                    const elementPosition = contentRef.current.getBoundingClientRect().top;
                                                    const offsetPosition = elementPosition + window.pageYOffset - 100;
                                                    window.scrollTo({
                                                        top: offsetPosition,
                                                        behavior: 'smooth'
                                                    });
                                                }
                                            }, 150);
                                        }}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                                            activeTab === tab.id
                                                ? 'bg-blue-600 text-white shadow-md'
                                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        <span className="mr-2">{tab.icon}</span>
                                        {tab.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderTabContent = () => {
        switch (activeTab) {
            case 'wizard':
                return (
                    <div className="space-y-6">
                        <SMSFCalculatorWizard 
                            smsfState={{
                                smsfData,
                                smsfAdminFees,
                                smsfAccountingFees,
                                smsfAuditFees
                            }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData) setSmsfData(newState.smsfData);
                                if (newState.smsfAdminFees !== undefined) setSmsfAdminFees(newState.smsfAdminFees);
                                if (newState.smsfAccountingFees !== undefined) setSmsfAccountingFees(newState.smsfAccountingFees);
                                if (newState.smsfAuditFees !== undefined) setSmsfAuditFees(newState.smsfAuditFees);
                                if (newState.smsfConcessionalUsed !== undefined) setSmsfConcessionalUsed(newState.smsfConcessionalUsed);
                                if (newState.smsfNonConcessionalUsed !== undefined) setSmsfNonConcessionalUsed(newState.smsfNonConcessionalUsed);
                                if (newState.smsfPropertyValue !== undefined) setSmsfPropertyValue(newState.smsfPropertyValue);
                                if (newState.smsfPropertyLoan !== undefined) setSmsfPropertyLoan(newState.smsfPropertyLoan);
                                if (newState.totalAssets !== undefined) {
                                    // Update individual asset allocations if needed
                                }
                            }}
                            onComplete={(data) => {
                                // When wizard completes, switch to configuration tab
                                setActiveTab('configuration');
                            }}
                        />
                    </div>
                );

            case 'dashboard':
                return (
                    <div className="space-y-6">
                        <SMSFUnifiedDashboard 
                            smsfState={{
                                smsfData,
                                totalAssets: smsfData?.totalAssets || smsfData?.fundBalance || 0,
                                smsfAuditFees,
                                smsfAdminFees,
                                smsfAccountingFees,
                                smsfConcessionalUsed,
                                smsfNonConcessionalUsed,
                                yourTbcUsed,
                                partnerTbcUsed,
                                smsfInHouseAssets,
                                smsfRelatedPartyLoans,
                                smsfPropertyValue,
                                smsfPropertyLoan
                            }}
                            onNavigate={(tabId) => setActiveTab(tabId)}
                        />
                    </div>
                );

            case 'comparison':
                return (
                    <div className="space-y-6">
                        <SMSFvsIndustryComparison 
                            smsfState={{
                                smsfData,
                                smsfAuditFees,
                                smsfAdminFees,
                                smsfAccountingFees
                            }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData) setSmsfData(newState.smsfData);
                                if (newState.smsfAuditFees !== undefined) setSmsfAuditFees(newState.smsfAuditFees);
                                if (newState.smsfAdminFees !== undefined) setSmsfAdminFees(newState.smsfAdminFees);
                                if (newState.smsfAccountingFees !== undefined) setSmsfAccountingFees(newState.smsfAccountingFees);
                            }}
                        />
                    </div>
                );

            case 'configuration':
                return (
                    <div className="space-y-6">
                        {/* Transfer to Other Calculators */}
                        <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg border-2 border-blue-300">
                            <h3 className="text-xl font-bold text-gray-800 mb-3">🔄 Transfer to Retirement Calculators</h3>
                            <p className="text-gray-700 mb-4">
                                Transfer the <strong>selected member's</strong> proportional share of SMSF assets to retirement planning calculators.
                            </p>
                            
                            {/* Member Selector */}
                            {(() => {
                                const derivedMembers = getDerivedMembers();
                                return derivedMembers.length > 0 && (
                                    <div className="mb-4 p-4 bg-white rounded-lg border-2 border-blue-300">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Select Member to Transfer:
                                        </label>
                                        <select
                                            value={selectedMemberId || derivedMembers[0]?.name || ''}
                                            onChange={(e) => setSelectedMemberId(e.target.value)}
                                            className="w-full px-4 py-2 border-2 border-blue-300 rounded-lg bg-white font-semibold text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            {derivedMembers.map((member, index) => {
                                                const memberAlloc = getMemberAssetAllocation();
                                                const totalFundBalance = derivedMembers.reduce((sum, m) => sum + (m.balance || 0), 0);
                                                const proportion = memberAlloc && member.name === (selectedMemberId || derivedMembers[0]?.name)
                                                    ? (memberAlloc.proportion * 100).toFixed(1)
                                                    : ((member.balance || 0) / totalFundBalance * 100).toFixed(1);
                                                return (
                                                    <option key={member.name || `member-${index}`} value={member.name}>
                                                        {member.name} ({member.age || 'Age not set'}) - ${(member.balance || 0).toLocaleString()} ({proportion}% of fund)
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    
                                    {(() => {
                                        const memberAlloc = getMemberAssetAllocation();
                                        if (!memberAlloc) return null;
                                        const { member, proportion, productiveAssets, nonProductiveAssets } = memberAlloc;
                                        const prodTotal = (productiveAssets.shares || 0) + (productiveAssets.infrastructure || 0) + (productiveAssets.cash || 0);
                                        return (
                                            <div className="mt-3 text-sm text-gray-700 bg-blue-50 p-3 rounded">
                                                <strong>📊 {member.name}'s Assets ({(proportion * 100).toFixed(1)}% of fund):</strong>
                                                <ul className="mt-2 space-y-1">
                                                    <li>• Super: ${prodTotal.toLocaleString()} (Shares ${(productiveAssets.shares || 0).toLocaleString()} + Infrastructure ${(productiveAssets.infrastructure || 0).toLocaleString()} + Cash ${(productiveAssets.cash || 0).toLocaleString()})</li>
                                                    {nonProductiveAssets.commercialProperty > 0 && <li>• Commercial Property: ${(nonProductiveAssets.commercialProperty || 0).toLocaleString()} (Rent ${(nonProductiveAssets.commercialRentalIncome || 0).toLocaleString()}/yr)</li>}
                                                    {nonProductiveAssets.residentialProperty > 0 && <li>• Residential Property: ${(nonProductiveAssets.residentialProperty || 0).toLocaleString()}</li>}
                                                    {nonProductiveAssets.collectibles > 0 && <li>• Collectibles: ${(nonProductiveAssets.collectibles || 0).toLocaleString()}</li>}
                                                </ul>
                                            </div>
                                        );
                                    })()}
                                    </div>
                                );
                            })()}
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <button
                                    onClick={() => {
                                        // Get member's proportional allocation
                                        const memberAlloc = getMemberAssetAllocation();
                                        if (!memberAlloc) {
                                            alert('⚠️ Please add fund members in the Configuration tab first.');
                                            return;
                                        }

                                        const { member, proportion, productiveAssets, nonProductiveAssets } = memberAlloc;
                                        
                                        // Extract all members for partner data
                                        const members = getDerivedMembers();
                                        const member2 = members[1] || {};
                                        
                                        // Calculate member's productive super balance (shares + infrastructure + cash)
                                        const memberShares = productiveAssets.shares || 0;
                                        const memberInfrastructure = productiveAssets.infrastructure || 0;
                                        const memberCash = productiveAssets.cash || 0;
                                        const memberProductiveBalance = memberShares + memberInfrastructure + memberCash;
                                        
                                        // Compute percentages of member's productive assets
                                        const ausSharesPct = memberProductiveBalance > 0 ? (memberShares / memberProductiveBalance) * 100 : 0;
                                        const infrastructurePct = memberProductiveBalance > 0 ? (memberInfrastructure / memberProductiveBalance) * 100 : 0;
                                        const cashPct = memberProductiveBalance > 0 ? (memberCash / memberProductiveBalance) * 100 : 0;
                                        const bondsPct = 0;
                                        const intlPct = 0;
                                        const sp500Pct = 0;
                                        
                                        // Member's non-productive assets (proportional)
                                        const memberCommercialProp = nonProductiveAssets.commercialProperty || 0;
                                        const memberCommercialRent = nonProductiveAssets.commercialRentalIncome || 0;
                                        const memberResidentialProp = nonProductiveAssets.residentialProperty || 0;
                                        const memberResidentialRent = nonProductiveAssets.residentialRentalIncome || 0;
                                        const memberCollectibles = nonProductiveAssets.collectibles || 0;
                                        
                                        // Calculate member's proportional fees
                                        const memberAdminFees = (smsfAdminFees || 0) * proportion;
                                        
                                        // Compute earning vs non-earning totals for member
                                        const nonEarningAssetsValue = memberCommercialProp + memberResidentialProp + memberCollectibles;
                                        const earningAssetsValue = memberProductiveBalance;
                                        const earningAssetsPct = memberProductiveBalance > 0 ? (earningAssetsValue / memberProductiveBalance) * 100 : 0;
                                        const nonEarningAssetsPct = memberProductiveBalance > 0 ? (nonEarningAssetsValue / memberProductiveBalance) * 100 : 0;
                                        
                                        const transferData = {
                                            yourAge: member.age || 65,
                                            yourBalance: memberProductiveBalance, // Member's proportional share only
                                            yourRetirementAge: member.retirementAge || 67,
                                            yourSalary: 0,
                                            partnerAge: member2.age || 65,
                                            partnerBalance: 0,
                                            partnerRetirementAge: member2.retirementAge || 67,
                                            partnerSalary: 0,
                                            relationshipStatus: members.length > 1 ? 'couple' : 'single',
                                            adminFees: memberAdminFees, // Member's proportional fees
                                            investmentFees: 0.5,
                                            insuranceFees: 0,
                                            otherIncome: 0,
                                            otherAssets: 0,
                                            isHomeowner: true,
                                            // Member's allocation percentages
                                            sp500: sp500Pct,
                                            ausShares: ausSharesPct,
                                            intlShares: intlPct,
                                            bonds: bondsPct,
                                            cash: cashPct,
                                            // Infrastructure data
                                            infrastructurePct: infrastructurePct,
                                            infrastructureValue: memberInfrastructure,
                                            infrastructureReturnRate: 6,
                                            // Member's proportional non-earning assets
                                            residentialPropertyValue: memberResidentialProp,
                                            residentialRentalIncome: memberResidentialRent,
                                            residentialPropertyRentalYield: memberResidentialProp > 0 ? (memberResidentialRent / memberResidentialProp) * 100 : 0,
                                            residentialPropertyCapitalGrowth: assetValuationData?.residentialPropertyCapitalGrowth || 4,
                                            commercialPropertyValue: memberCommercialProp,
                                            commercialRentalIncome: memberCommercialRent,
                                            commercialPropertyRentalYield: memberCommercialProp > 0 ? (memberCommercialRent / memberCommercialProp) * 100 : 0,
                                            commercialPropertyCapitalGrowth: assetValuationData?.commercialPropertyCapitalGrowth || 4,
                                            collectiblesValue: memberCollectibles,
                                            collectiblesCapitalGrowth: 0
                                        };
                                        
                                        const formattedData = CalculatorBridge.smsfToAccumulation(transferData);
                                        CalculatorBridge.storeTransferData(formattedData, 'smsf', 'accumulation');
                                        
                                        // Show alert with member details
                                        alert(`✅ ${member.name}'s Data Transferred!\n\nTransferred ${(proportion * 100).toFixed(1)}% of fund assets ($${memberProductiveBalance.toLocaleString()}) to Accumulation Calculator.`);
                                        
                                        // Navigate to Accumulation calculator
                                        window.location.hash = '#working-accumulation';
                                    }}
                                    className="px-6 py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition shadow-lg"
                                >
                                    <div className="text-lg font-bold mb-1">📈 Accumulation</div>
                                    <div className="text-xs opacity-90">Project growth to retirement</div>
                                </button>
                                
                                <button
                                    onClick={() => {
                                        console.log('🔍 DEBUG: Advanced button clicked');
                                        
                                        // Get member's proportional allocation
                                        const memberAlloc = getMemberAssetAllocation();
                                        if (!memberAlloc) {
                                            alert('⚠️ Please add fund members in the Configuration tab first.');
                                            return;
                                        }

                                        const { member, proportion, productiveAssets, nonProductiveAssets } = memberAlloc;
                                        console.log('📊 Member allocation:', memberAlloc);
                                        
                                        // Extract member data
                                        const members = smsfData?.members || [];
                                        const member2 = members[1] || {};
                                        
                                        // Calculate member's productive super balance (shares + infrastructure + cash)
                                        const memberShares = productiveAssets.shares || 0;
                                        const memberInfrastructure = productiveAssets.infrastructure || 0;
                                        const memberCash = productiveAssets.cash || 0;
                                        const memberProductiveBalance = memberShares + memberInfrastructure + memberCash;
                                        
                                        // Compute percentages of member's productive assets
                                        const sharesPct = memberProductiveBalance > 0 ? (memberShares / memberProductiveBalance) * 100 : 0;
                                        const infrastructurePct = memberProductiveBalance > 0 ? (memberInfrastructure / memberProductiveBalance) * 100 : 0;
                                        const cashPct = memberProductiveBalance > 0 ? (memberCash / memberProductiveBalance) * 100 : 0;
                                        
                                        // Member's non-productive assets
                                        const memberCommercialProp = nonProductiveAssets.commercialProperty || 0;
                                        const memberCommercialRent = nonProductiveAssets.commercialRentalIncome || 0;
                                        const memberResidentialProp = nonProductiveAssets.residentialProperty || 0;
                                        const memberResidentialRent = nonProductiveAssets.residentialRentalIncome || 0;
                                        const memberCollectibles = nonProductiveAssets.collectibles || 0;
                                        
                                        // Calculate member's proportional fees
                                        const memberAdminFees = (smsfAdminFees || 0) * proportion;
                                        const memberAccountingFees = (smsfAccountingFees || 0) * proportion;
                                        const memberAuditFees = (smsfAuditFees || 0) * proportion;
                                        const totalMemberFees = memberAdminFees + memberAccountingFees + memberAuditFees;
                                        
                                        console.log('💰 Member fees:', { 
                                            proportion: proportion.toFixed(3), 
                                            adminFees: memberAdminFees.toFixed(2),
                                            totalFees: totalMemberFees.toFixed(2)
                                        });
                                        
                                        const transferData = {
                                            yourAge: member.age || 65,
                                            yourBalance: memberProductiveBalance, // Member's productive assets only
                                            yourSuper: memberProductiveBalance, // Member's share of super
                                            yourRetirementAge: member.retirementAge || 67,
                                            yourSalary: 0,
                                            yourTbcUsed: member.tbr || 0,
                                            partnerAge: member2.age || 65,
                                            partnerBalance: 0,
                                            partnerSuper: 0,
                                            partnerRetirementAge: member2.retirementAge || 67,
                                            partnerSalary: 0,
                                            partnerTbcUsed: member2.tbr || 0,
                                            relationshipStatus: members.length > 1 ? 'couple' : 'single',
                                            adminFees: totalMemberFees, // Member's proportional share of all fees
                                            adminFeePercentage: 0.10,
                                            investmentFees: 0.5,
                                            insuranceFees: member.insuranceCover ? (member.insuranceCover * 0.001) : 0, // 0.1% of cover
                                            otherIncome: 0,
                                            otherAssets: 0,
                                            isHomeowner: true,
                                            // Asset percentages based on member's productive assets
                                            sp500: 0,
                                            ausShares: sharesPct,
                                            intlShares: 0,
                                            bonds: 0,
                                            cash: cashPct,
                                            // Infrastructure
                                            infrastructurePct: infrastructurePct,
                                            infrastructureValue: memberInfrastructure,
                                            infrastructureReturnRate: 6,
                                            // Member's share of alternative assets
                                            residentialPropertyValue: memberResidentialProp,
                                            residentialRentalIncome: memberResidentialRent,
                                            residentialPropertyRentalYield: assetValuationData?.residentialPropertyRentalYield || 0,
                                            residentialPropertyCapitalGrowth: assetValuationData?.residentialPropertyCapitalGrowth || 4.0,
                                            commercialPropertyValue: memberCommercialProp,
                                            commercialRentalIncome: memberCommercialRent,
                                            commercialPropertyRentalYield: assetValuationData?.commercialPropertyRentalYield || 0,
                                            commercialPropertyCapitalGrowth: assetValuationData?.commercialPropertyCapitalGrowth || 4.0,
                                            collectiblesValue: memberCollectibles,
                                            collectiblesCapitalGrowth: 0,
                                            // Asset details
                                            assetDetails: assetValuationData?.assetDetails || null
                                        };
                                        
                                        console.log('DEBUG: Asset Valuation Data:', JSON.stringify(assetValuationData, null, 2));
                                        console.log('DEBUG: Full SMSF State object (JSON):', JSON.stringify(smsfState, null, 2));
                                        console.log('DEBUG: SMSF State values:');
                                        console.log('  - residentialPropertyValue:', smsfState?.residentialPropertyValue);
                                        console.log('  - residentialRentalIncome:', smsfState?.residentialRentalIncome);
                                        console.log('  - residentialPropertyCapitalGrowth:', smsfState?.residentialPropertyCapitalGrowth);
                                        console.log('  - commercialPropertyValue:', smsfState?.commercialPropertyValue);
                                        console.log('  - commercialRentalIncome:', smsfState?.commercialRentalIncome);
                                        console.log('  - commercialPropertyCapitalGrowth:', smsfState?.commercialPropertyCapitalGrowth);
                                        console.log('  - assetValues:', smsfState?.assetValues);
                                        console.log('  - assetAllocationTargets:', assetAllocationTargets);
                                        console.log('  - assetAllocationActual:', assetAllocationActual);
                                        console.log('DEBUG: Raw transfer data from SMSF:', JSON.stringify(transferData, null, 2));
                                        const formattedData = CalculatorBridge.smsfToAdvanced(transferData);
                                        console.log('DEBUG: Formatted data for Advanced calculator:', JSON.stringify(formattedData, null, 2));
                                        CalculatorBridge.storeTransferData(formattedData, 'smsf', 'advanced');
                                        
                                        // Navigate to Advanced calculator
                                        window.location.hash = '#advanced';
                                        
                                        alert('✅ Data transferred to Advanced Calculator!\n\nThe calculator will auto-populate with your SMSF data.\n\nTip: Try different withdrawal strategies (Fixed, Dynamic SWR, Floor & Ceiling).');
                                    }}
                                    className="px-6 py-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition shadow-lg"
                                >
                                    <div className="text-lg font-bold mb-1">🚀 Advanced</div>
                                    <div className="text-xs opacity-90">Comprehensive analysis</div>
                                </button>
                            </div>
                            
                            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                                <p className="text-sm text-blue-700">
                                    <strong>💡 How it works:</strong> Your SMSF data (balances, ages, fees) is transferred to the selected calculator 
                                    and auto-populated. The transfer is valid for 5 minutes. If you need to transfer again, just click the button.
                                </p>
                            </div>
                        </div>
                        
                        <SMSFConfigurationForm
                            inputsLocked={inputsLocked} 
                            onConfigurationChange={handleConfigurationChange}
                            initialConfig={{ ...smsfData, fundBalance: smsfData?.balance ?? smsfData?.totalAssets ?? 0 }}
                        />
                        
                        {/* Member Profile Configuration */}
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-lg font-semibold mb-4">Member Profile</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <InfoTooltip content="Your current age is used to calculate retirement planning timelines and determine applicable SMSF rules and contribution limits.">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Age
                                    </label>
                                    </InfoTooltip>
                                    <input
                                        {...getNumericInputProps()}
                                        value={memberProfile.age}
                                        onChange={(e) => handleMemberProfileChange({...memberProfile, age: parseInt(e.target.value)})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <InfoTooltip content="Your risk tolerance determines the recommended asset allocation and investment strategy for your SMSF. Low risk focuses on capital preservation, high risk seeks maximum growth.">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Risk Tolerance
                                    </label>
                                    </InfoTooltip>
                                    <select
                                        value={memberProfile.riskTolerance}
                                        onChange={(e) => handleMemberProfileChange({...memberProfile, riskTolerance: e.target.value})}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="LOW">Low</option>
                                        <option value="MEDIUM">Medium</option>
                                        <option value="HIGH">High</option>
                                        <option value="VERY_HIGH">Very High</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'fees':
                return (
                    <div className="space-y-6">
                        <SMSFEnhancedFeeAnalysis 
                            smsfState={{
                                smsfData,
                                memberProfile,
                                constraints,
                                smsfTrusteeCount,
                                smsfEstablishedDate,
                                smsfAuditFees,
                                smsfAdminFees,
                                smsfAccountingFees,
                                smsfConcessionalUsed,
                                smsfNonConcessionalUsed,
                                yourTbcUsed,
                                partnerTbcUsed,
                                smsfDirectProperty,
                                smsfPropertyValue,
                                smsfPropertyLoan,
                                smsfPropertyInterestRate,
                                smsfPropertyLoanTerm,
                                smsfInHouseAssets,
                                smsfRelatedPartyLoans,
                                smsfCommercialProperty,
                                smsfPropertyRentalYield,
                                deathBenefitNominations,
                                reversionaryPensionEnabled,
                                reversionaryPensionAmount,
                                spouseAge,
                                beneficiaries,
                                taxFreeComponent,
                                taxableComponent
                            }}
                            setSmsfState={(newState) => {
                                // Update individual state variables based on newState
                                if (newState.smsfData) setSmsfData(newState.smsfData);
                                if (newState.memberProfile) setMemberProfile(newState.memberProfile);
                                if (newState.constraints) setConstraints(newState.constraints);
                                if (newState.smsfTrusteeCount !== undefined) setSmsfTrusteeCount(newState.smsfTrusteeCount);
                                if (newState.smsfEstablishedDate !== undefined) setSmsfEstablishedDate(newState.smsfEstablishedDate);
                                if (newState.smsfAuditFees !== undefined) setSmsfAuditFees(newState.smsfAuditFees);
                                if (newState.smsfAdminFees !== undefined) setSmsfAdminFees(newState.smsfAdminFees);
                                if (newState.smsfAccountingFees !== undefined) setSmsfAccountingFees(newState.smsfAccountingFees);
                                if (newState.smsfConcessionalUsed !== undefined) setSmsfConcessionalUsed(newState.smsfConcessionalUsed);
                                if (newState.smsfNonConcessionalUsed !== undefined) setSmsfNonConcessionalUsed(newState.smsfNonConcessionalUsed);
                                if (newState.yourTbcUsed !== undefined) setYourTbcUsed(newState.yourTbcUsed);
                                if (newState.partnerTbcUsed !== undefined) setPartnerTbcUsed(newState.partnerTbcUsed);
                                if (newState.smsfDirectProperty !== undefined) setSmsfDirectProperty(newState.smsfDirectProperty);
                                if (newState.smsfPropertyValue !== undefined) setSmsfPropertyValue(newState.smsfPropertyValue);
                                if (newState.smsfPropertyLoan !== undefined) setSmsfPropertyLoan(newState.smsfPropertyLoan);
                                if (newState.smsfPropertyInterestRate !== undefined) setSmsfPropertyInterestRate(newState.smsfPropertyInterestRate);
                                if (newState.smsfPropertyLoanTerm !== undefined) setSmsfPropertyLoanTerm(newState.smsfPropertyLoanTerm);
                                if (newState.smsfInHouseAssets !== undefined) setSmsfInHouseAssets(newState.smsfInHouseAssets);
                                if (newState.smsfRelatedPartyLoans !== undefined) setSmsfRelatedPartyLoans(newState.smsfRelatedPartyLoans);
                                if (newState.smsfCommercialProperty !== undefined) setSmsfCommercialProperty(newState.smsfCommercialProperty);
                                if (newState.smsfPropertyRentalYield !== undefined) setSmsfPropertyRentalYield(newState.smsfPropertyRentalYield);
                                if (newState.deathBenefitNominations !== undefined) setDeathBenefitNominations(newState.deathBenefitNominations);
                                if (newState.reversionaryPensionEnabled !== undefined) setReversionaryPensionEnabled(newState.reversionaryPensionEnabled);
                                if (newState.reversionaryPensionAmount !== undefined) setReversionaryPensionAmount(newState.reversionaryPensionAmount);
                                if (newState.spouseAge !== undefined) setSpouseAge(newState.spouseAge);
                                if (newState.beneficiaries !== undefined) setBeneficiaries(newState.beneficiaries);
                                if (newState.taxFreeComponent !== undefined) setTaxFreeComponent(newState.taxFreeComponent);
                                if (newState.taxableComponent !== undefined) setTaxableComponent(newState.taxableComponent);
                            }}
                        />
                    </div>
                );

            case 'members':
                return (() => {
                    const derivedMembers = getDerivedMembers();
                    return (
                        <div className="space-y-6">
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border-2 border-blue-300">
                                <h2 className="text-2xl font-bold text-gray-800 mb-2">👥 Members Summary</h2>
                                <p className="text-gray-700 mb-2">
                                    View all SMSF members and their details. Member data is managed in the <strong>Personal/Fund Details</strong> tab (Configuration).
                                </p>
                                <button
                                    onClick={() => setActiveTab('configuration')}
                                    className="text-sm text-blue-600 hover:text-blue-800 font-semibold underline"
                                >
                                    → Go to Personal/Fund Details to edit member information
                                </button>
                            </div>

                            {/* Member List - Read Only */}
                            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                                <h3 className="text-lg font-semibold mb-4">SMSF Members</h3>
                                
                                {derivedMembers.length > 0 ? (
                                    <div className="space-y-4">
                                        {derivedMembers.map((member, index) => (
                                            <div key={index} className="p-4 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg border-2 border-gray-300">
                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                                    <div>
                                                        <div className="text-xs font-medium text-gray-500 uppercase">Name</div>
                                                        <div className="text-lg font-semibold text-gray-800">{member.name}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-xs font-medium text-gray-500 uppercase">Age</div>
                                                        <div className="text-lg font-semibold text-gray-800">{member.age || 'Not set'}</div>
                                                        {member.dob && <div className="text-xs text-gray-500">DOB: {member.dob}</div>}
                                                    </div>
                                                    <div>
                                                        <div className="text-xs font-medium text-gray-500 uppercase">Account Balance</div>
                                                        <div className="text-lg font-semibold text-green-600">${(member.balance || 0).toLocaleString()}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-xs font-medium text-gray-500 uppercase">Member Type</div>
                                                        <div className="text-sm font-medium text-gray-700 capitalize">{member.memberType || 'accumulation'}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-xs font-medium text-gray-500 uppercase">TBC Used</div>
                                                        <div className="text-sm font-medium text-gray-700">${(member.tbcUsed || 0).toLocaleString()}</div>
                                                    </div>
                                                    <div>
                                                        <div className="text-xs font-medium text-gray-500 uppercase">FY Contributions</div>
                                                        <div className="text-xs text-gray-600">
                                                            Conc: ${(member.concessionalUsed || 0).toLocaleString()}<br/>
                                                            Non-Conc: ${(member.nonConcessionalUsed || 0).toLocaleString()}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <p className="text-gray-500 mb-4">No members added yet.</p>
                                        <button
                                            onClick={() => setActiveTab('configuration')}
                                            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition"
                                        >
                                            → Add Members in Personal/Fund Details
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Member Summary */}
                            {derivedMembers.length > 0 && (
                                <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                                    <h3 className="text-lg font-semibold mb-4">Fund Summary</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div className="p-4 bg-blue-50 rounded-lg">
                                            <div className="text-sm text-gray-600">Total Members</div>
                                            <div className="text-2xl font-bold text-blue-600">
                                                {derivedMembers.length}
                                            </div>
                                        </div>
                                        <div className="p-4 bg-green-50 rounded-lg">
                                            <div className="text-sm text-gray-600">Total Balance</div>
                                            <div className="text-2xl font-bold text-green-600">
                                                ${derivedMembers.reduce((sum, m) => sum + (m.balance || 0), 0).toLocaleString()}
                                            </div>
                                        </div>
                                        <div className="p-4 bg-purple-50 rounded-lg">
                                            <div className="text-sm text-gray-600">Average Age</div>
                                            <div className="text-2xl font-bold text-purple-600">
                                                {derivedMembers.length > 0 
                                                    ? Math.round(derivedMembers.reduce((sum, m) => sum + (m.age || 0), 0) / derivedMembers.length)
                                                    : 0}
                                            </div>
                                        </div>
                                        <div className="p-4 bg-orange-50 rounded-lg">
                                            <div className="text-sm text-gray-600">Total TBC Used</div>
                                            <div className="text-2xl font-bold text-orange-600">
                                                ${derivedMembers.reduce((sum, m) => sum + (m.tbcUsed || 0), 0).toLocaleString()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })();

            case 'contributions':
                return (
                    <div className="space-y-6">
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border-2 border-green-300">
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">💰 Contributions Hub</h2>
                            <p className="text-gray-700 mb-4">
                                Access all contribution calculators and tools. Maximize your super contributions while staying within ATO limits.
                            </p>
                        </div>

                        {/* Quick Links to Contribution Tools */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <button
                                onClick={() => setActiveTab('carry_forward')}
                                className="p-6 bg-white rounded-lg shadow-md border-2 border-gray-200 hover:border-blue-400 hover:shadow-lg transition text-left"
                            >
                                <div className="text-3xl mb-2">📦</div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-1">Carry-Forward</h3>
                                <p className="text-sm text-gray-600">Use unused concessional caps from previous years</p>
                            </button>

                            <button
                                onClick={() => setActiveTab('co_contribution')}
                                className="p-6 bg-white rounded-lg shadow-md border-2 border-gray-200 hover:border-green-400 hover:shadow-lg transition text-left"
                            >
                                <div className="text-3xl mb-2">💚</div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-1">Co-Contribution</h3>
                                <p className="text-sm text-gray-600">Government matching contributions calculator</p>
                            </button>

                            <button
                                onClick={() => setActiveTab('downsizer')}
                                className="p-6 bg-white rounded-lg shadow-md border-2 border-gray-200 hover:border-purple-400 hover:shadow-lg transition text-left"
                            >
                                <div className="text-3xl mb-2">🏡</div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-1">Downsizer</h3>
                                <p className="text-sm text-gray-600">Contribute up to $300k from home sale</p>
                            </button>

                            <button
                                onClick={() => setActiveTab('spouse_split')}
                                className="p-6 bg-white rounded-lg shadow-md border-2 border-gray-200 hover:border-pink-400 hover:shadow-lg transition text-left"
                            >
                                <div className="text-3xl mb-2">💑</div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-1">Spouse Splitting</h3>
                                <p className="text-sm text-gray-600">Split contributions with your spouse</p>
                            </button>

                            <button
                                onClick={() => setActiveTab('work_test')}
                                className="p-6 bg-white rounded-lg shadow-md border-2 border-gray-200 hover:border-yellow-400 hover:shadow-lg transition text-left"
                            >
                                <div className="text-3xl mb-2">⏱️</div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-1">Work Test</h3>
                                <p className="text-sm text-gray-600">Check work test requirements for contributions</p>
                            </button>

                            <button
                                onClick={() => setActiveTab('fhss')}
                                className="p-6 bg-white rounded-lg shadow-md border-2 border-gray-200 hover:border-orange-400 hover:shadow-lg transition text-left"
                            >
                                <div className="text-3xl mb-2">🏡</div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-1">FHSS</h3>
                                <p className="text-sm text-gray-600">First Home Super Saver Scheme calculator</p>
                            </button>
                        </div>

                        {/* Contribution Caps Summary */}
                        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
                            <h3 className="text-lg font-semibold mb-4">📊 Contribution Caps ({FinancialConfig._metadata?.financialYear || '2025-26'})</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-gray-700">Concessional Cap</span>
                                        <span className="text-lg font-bold text-blue-600">${FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL.toLocaleString()}</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div 
                                            className="bg-blue-600 h-2 rounded-full" 
                                            style={{ width: `${Math.min(100, ((smsfConcessionalUsed || 0) / FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL) * 100)}%` }}
                                        ></div>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                        Used: ${(smsfConcessionalUsed || 0).toLocaleString()} | Remaining: ${Math.max(0, FinancialConfig.CONTRIBUTION_CAPS.CONCESSIONAL - (smsfConcessionalUsed || 0)).toLocaleString()}
                                    </div>
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-gray-700">Non-Concessional Cap</span>
                                        <span className="text-lg font-bold text-green-600">${FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL.toLocaleString()}</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div 
                                            className="bg-green-600 h-2 rounded-full" 
                                            style={{ width: `${Math.min(100, ((smsfNonConcessionalUsed || 0) / FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL) * 100)}%` }}
                                        ></div>
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                        Used: ${(smsfNonConcessionalUsed || 0).toLocaleString()} | Remaining: ${Math.max(0, FinancialConfig.CONTRIBUTION_CAPS.NON_CONCESSIONAL - (smsfNonConcessionalUsed || 0)).toLocaleString()}
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                <p className="text-sm text-gray-700">
                                    <strong>💡 Tip:</strong> Use the Carry-Forward calculator to access unused concessional caps from previous years if your Total Super Balance was below $500k on June 30.
                                </p>
                            </div>
                        </div>
                    </div>
                );

            case 'compliance':
                return (
                    <div className="space-y-6">
                        <SMSFComplianceDashboard 
                            smsfState={{
                                smsfData: {
                                    balance: smsfData?.balance || smsfData?.fundBalance || 0,
                                    fundBalance: smsfData?.fundBalance || smsfData?.balance || 0,
                                    concessionalUsed: smsfConcessionalUsed,
                                    nonConcessionalUsed: smsfNonConcessionalUsed,
                                    yourTbcUsed: yourTbcUsed,
                                    partnerTbcUsed: partnerTbcUsed,
                                    inHouseAssets: smsfInHouseAssets || 0
                                }
                            }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData?.yourTbcUsed !== undefined) setYourTbcUsed(newState.smsfData.yourTbcUsed);
                                if (newState.smsfData?.partnerTbcUsed !== undefined) setPartnerTbcUsed(newState.smsfData.partnerTbcUsed);
                            }}
                        />
                    </div>
                );

            case 'investment':
                return (
                    <div className="space-y-6">
                        
                        {/* Direct Property Investment - READ-ONLY SUMMARY */}
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-lg shadow-md border-2 border-blue-200">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800">📊 Direct Property Investment Summary</h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                        <span className="mr-1">🔒</span>
                                        Read-only view. Edit properties in the <button onClick={() => setActiveTab('valuation')} className="text-blue-600 hover:text-blue-800 font-semibold underline">Asset Valuation</button> tab.
                                    </p>
                                </div>
                            </div>
                            
                            {(assetValuationData.residentialPropertyValue > 0 || assetValuationData.commercialPropertyValue > 0) ? (
                                <div className="space-y-6">
                                    {/* Residential Property */}
                                    {assetValuationData.residentialPropertyValue > 0 && (
                                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                                            <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                                                <span className="mr-2">🏘️</span>
                                                Residential Property
                                            </h4>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-600 mb-1">Total Value</label>
                                                    <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                                                        ${assetValuationData.residentialPropertyValue.toLocaleString()}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-600 mb-1">Annual Rental Income</label>
                                                    <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                                                        ${assetValuationData.residentialRentalIncome.toLocaleString()}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-600 mb-1">Rental Yield</label>
                                                    <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                                                        {assetValuationData.residentialPropertyRentalYield.toFixed(2)}%
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-600 mb-1">Capital Growth</label>
                                                    <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                                                        {assetValuationData.residentialPropertyCapitalGrowth.toFixed(2)}% p.a.
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Commercial Property */}
                                    {assetValuationData.commercialPropertyValue > 0 && (
                                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                                            <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                                                <span className="mr-2">🏢</span>
                                                Commercial Property
                                            </h4>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-600 mb-1">Total Value</label>
                                                    <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                                                        ${assetValuationData.commercialPropertyValue.toLocaleString()}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-600 mb-1">Annual Rental Income</label>
                                                    <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                                                        ${assetValuationData.commercialRentalIncome.toLocaleString()}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-600 mb-1">Rental Yield</label>
                                                    <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                                                        {assetValuationData.commercialPropertyRentalYield.toFixed(2)}%
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-600 mb-1">Capital Growth</label>
                                                    <div className="px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-700">
                                                        {assetValuationData.commercialPropertyCapitalGrowth.toFixed(2)}% p.a.
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* LRBA Loan Summary */}
                                    {assetValuationData.totalLoanAmount > 0 && (
                                        <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                                            <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                                                <span className="mr-2">🏦</span>
                                                Limited Recourse Borrowing Arrangement (LRBA)
                                            </h4>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-600 mb-1">Total Loan Amount</label>
                                                    <div className="px-3 py-2 bg-white border border-amber-300 rounded-md text-gray-700 font-semibold">
                                                        ${assetValuationData.totalLoanAmount.toLocaleString()}
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-600 mb-1">Weighted Avg Interest Rate</label>
                                                    <div className="px-3 py-2 bg-white border border-amber-300 rounded-md text-gray-700 font-semibold">
                                                        {assetValuationData.avgInterestRate.toFixed(2)}% p.a.
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-600 mb-1">Weighted Avg Loan Term</label>
                                                    <div className="px-3 py-2 bg-white border border-amber-300 rounded-md text-gray-700 font-semibold">
                                                        {Math.round(assetValuationData.avgLoanTerm)} years
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-600 mb-1">Loan-to-Value Ratio (LVR)</label>
                                                    <div className="px-3 py-2 bg-white border border-amber-300 rounded-md text-gray-700 font-semibold">
                                                        {((assetValuationData.totalLoanAmount / (assetValuationData.residentialPropertyValue + assetValuationData.commercialPropertyValue)) * 100).toFixed(1)}%
                                                        {((assetValuationData.totalLoanAmount / (assetValuationData.residentialPropertyValue + assetValuationData.commercialPropertyValue)) * 100) > (SMSF_RATES_CONFIG.lrbaLimits.maxLoanToValueRatio * 100) && (
                                                            <span className="ml-2 text-red-600 font-bold">⚠️ Exceeds {Math.round(SMSF_RATES_CONFIG.lrbaLimits.maxLoanToValueRatio * 100)}% limit</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-500">
                                    <p className="text-lg mb-2">No property investments recorded</p>
                                    <p className="text-sm">
                                        Add properties in the <button onClick={() => setActiveTab('valuation')} className="text-blue-600 hover:text-blue-800 font-semibold underline">Asset Valuation</button> tab
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 'investment_ato':
                return (
                    <div className="space-y-6">
                        <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg border-2 border-blue-300">
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">⚖️ ATO-Compliant Investment Strategy Builder</h2>
                            <p className="text-gray-700">
                                Generate a professional, audit-ready investment strategy that meets all Regulation 4.09 requirements.
                            </p>
                        </div>
                        
                        <InvestmentStrategyBuilder
                            smsfState={{
                                fundName: (smsfData && smsfData.fundName) || '',
                                abn: (smsfData && smsfData.abn) || '',
                                establishedDate: smsfEstablishedDate || (smsfData && smsfData.establishedDate) || '',
                                members: (() => {
                                    const members = getDerivedMembers(); // Use derived members from Configuration
                                    if (Array.isArray(members) && members.length > 0) {
                                        return members.map((m) => ({
                                            name: m.name || '',
                                            age: m.age || 0,
                                            balance: m.balance != null ? m.balance : 0,
                                            retirementAge: m.retirementAge || 67
                                        }));
                                    }
                                    // Fallback to a single member synthesized from available profile data
                                    return [{
                                        name: (memberProfile && memberProfile.name) || '',
                                        age: (memberProfile && memberProfile.age) || 0,
                                        balance: (smsfData && (smsfData.balance || smsfData.totalAssets)) || 0,
                                        retirementAge: (memberProfile && memberProfile.retirementAge) || 67
                                    }];
                                })()
                            }}
                            onSave={(strategy) => {
                                // Save strategy to state
                                console.log('Strategy saved:', strategy);
                                setActiveTab('dashboard');
                            }}
                            onCancel={() => setActiveTab('investment')}
                        />
                    </div>
                );

            case 'strategy':
                return (
                    <div className="space-y-6">
                        <SMSFInvestmentStrategyBuilder 
                            smsfState={{
                                smsfData,
                                memberProfile,
                                constraints,
                                smsfTrusteeCount,
                                smsfEstablishedDate,
                                smsfAuditFees,
                                smsfAdminFees,
                                smsfAccountingFees,
                                smsfConcessionalUsed,
                                smsfNonConcessionalUsed,
                                yourTbcUsed,
                                partnerTbcUsed,
                                smsfDirectProperty,
                                smsfPropertyValue,
                                smsfPropertyLoan,
                                smsfPropertyInterestRate,
                                smsfPropertyLoanTerm,
                                smsfInHouseAssets,
                                smsfRelatedPartyLoans,
                                smsfCommercialProperty,
                                smsfPropertyRentalYield,
                                deathBenefitNominations,
                                reversionaryPensionEnabled,
                                reversionaryPensionAmount,
                                spouseAge,
                                beneficiaries,
                                taxFreeComponent,
                                taxableComponent
                            }}
                            setSmsfState={(newState) => {
                                // Update individual state variables based on newState
                                if (newState.smsfData) setSmsfData(newState.smsfData);
                                if (newState.memberProfile) setMemberProfile(newState.memberProfile);
                                if (newState.constraints) setConstraints(newState.constraints);
                                if (newState.smsfTrusteeCount !== undefined) setSmsfTrusteeCount(newState.smsfTrusteeCount);
                                if (newState.smsfEstablishedDate !== undefined) setSmsfEstablishedDate(newState.smsfEstablishedDate);
                                if (newState.smsfAuditFees !== undefined) setSmsfAuditFees(newState.smsfAuditFees);
                                if (newState.smsfAdminFees !== undefined) setSmsfAdminFees(newState.smsfAdminFees);
                                if (newState.smsfAccountingFees !== undefined) setSmsfAccountingFees(newState.smsfAccountingFees);
                                if (newState.smsfConcessionalUsed !== undefined) setSmsfConcessionalUsed(newState.smsfConcessionalUsed);
                                if (newState.smsfNonConcessionalUsed !== undefined) setSmsfNonConcessionalUsed(newState.smsfNonConcessionalUsed);
                                if (newState.yourTbcUsed !== undefined) setYourTbcUsed(newState.yourTbcUsed);
                                if (newState.partnerTbcUsed !== undefined) setPartnerTbcUsed(newState.partnerTbcUsed);
                                if (newState.smsfDirectProperty !== undefined) setSmsfDirectProperty(newState.smsfDirectProperty);
                                if (newState.smsfPropertyValue !== undefined) setSmsfPropertyValue(newState.smsfPropertyValue);
                                if (newState.smsfPropertyLoan !== undefined) setSmsfPropertyLoan(newState.smsfPropertyLoan);
                                if (newState.smsfPropertyInterestRate !== undefined) setSmsfPropertyInterestRate(newState.smsfPropertyInterestRate);
                                if (newState.smsfPropertyLoanTerm !== undefined) setSmsfPropertyLoanTerm(newState.smsfPropertyLoanTerm);
                                if (newState.smsfInHouseAssets !== undefined) setSmsfInHouseAssets(newState.smsfInHouseAssets);
                                if (newState.smsfRelatedPartyLoans !== undefined) setSmsfRelatedPartyLoans(newState.smsfRelatedPartyLoans);
                                if (newState.smsfCommercialProperty !== undefined) setSmsfCommercialProperty(newState.smsfCommercialProperty);
                                if (newState.smsfPropertyRentalYield !== undefined) setSmsfPropertyRentalYield(newState.smsfPropertyRentalYield);
                                if (newState.deathBenefitNominations !== undefined) setDeathBenefitNominations(newState.deathBenefitNominations);
                                if (newState.reversionaryPensionEnabled !== undefined) setReversionaryPensionEnabled(newState.reversionaryPensionEnabled);
                                if (newState.reversionaryPensionAmount !== undefined) setReversionaryPensionAmount(newState.reversionaryPensionAmount);
                                if (newState.spouseAge !== undefined) setSpouseAge(newState.spouseAge);
                                if (newState.beneficiaries !== undefined) setBeneficiaries(newState.beneficiaries);
                                if (newState.taxFreeComponent !== undefined) setTaxFreeComponent(newState.taxFreeComponent);
                                if (newState.taxableComponent !== undefined) setTaxableComponent(newState.taxableComponent);
                            }}
                        />
                    </div>
                );

            case 'valuation':
                return (
                    <div className="space-y-6">
                        <SMSFAssetValuationManager 
                            smsfState={smsfData}
                            setSmsfState={setSmsfData}
                            onValuationChange={(data) => {
                                console.log('Asset valuation data updated:', data);
                                window.smsfAssetValuationData = data;
                                
                                // Update fund total balance from asset valuation
                                if (data && data.assetValues) {
                                    const totalAssets = Object.values(data.assetValues).reduce((sum, val) => sum + (val || 0), 0);
                                    console.log('Updating fund balance from valuation:', totalAssets);
                                    setSmsfData(prev => ({
                                        ...prev,
                                        balance: totalAssets,
                                        totalAssets: totalAssets
                                    }));
                                }
                            }}
                            onAllocateToMembers={() => {
                                // Get asset breakdown from valuation data
                                const valuationData = window.smsfAssetValuationData || {};
                                const breakdown = {
                                    shares: valuationData?.productiveAssets?.shares || 0,
                                    infrastructure: valuationData?.productiveAssets?.alternatives || 0,
                                    cash: valuationData?.productiveAssets?.cash || 0,
                                    commercialProperty: valuationData?.commercialPropertyValue || 0,
                                    residentialProperty: valuationData?.residentialPropertyValue || 0,
                                    collectibles: valuationData?.assetValues?.collectibles || 0
                                };
                                setAssetBreakdownForAllocation(breakdown);
                                setShowAllocationModal(true);
                            }}
                        />
                    </div>
                );

            case 'risk':
                return (
                    <div className="space-y-6">
                        <SMSFRiskAssessmentEngine 
                            smsfState={{
                                smsfData,
                                memberProfile,
                                constraints,
                                smsfTrusteeCount,
                                smsfEstablishedDate,
                                smsfAuditFees,
                                smsfAdminFees,
                                smsfAccountingFees,
                                smsfConcessionalUsed,
                                smsfNonConcessionalUsed,
                                yourTbcUsed,
                                partnerTbcUsed,
                                smsfDirectProperty,
                                smsfPropertyValue,
                                smsfPropertyLoan,
                                smsfPropertyInterestRate,
                                smsfPropertyLoanTerm,
                                smsfInHouseAssets,
                                smsfRelatedPartyLoans,
                                smsfCommercialProperty,
                                smsfPropertyRentalYield,
                                deathBenefitNominations,
                                reversionaryPensionEnabled,
                                reversionaryPensionAmount,
                                spouseAge,
                                beneficiaries,
                                taxFreeComponent,
                                taxableComponent
                            }}
                            setSmsfState={(newState) => {
                                // Update individual state variables based on newState
                                if (newState.smsfData) setSmsfData(newState.smsfData);
                                if (newState.memberProfile) setMemberProfile(newState.memberProfile);
                                if (newState.constraints) setConstraints(newState.constraints);
                                if (newState.smsfTrusteeCount !== undefined) setSmsfTrusteeCount(newState.smsfTrusteeCount);
                                if (newState.smsfEstablishedDate !== undefined) setSmsfEstablishedDate(newState.smsfEstablishedDate);
                                if (newState.smsfAuditFees !== undefined) setSmsfAuditFees(newState.smsfAuditFees);
                                if (newState.smsfAdminFees !== undefined) setSmsfAdminFees(newState.smsfAdminFees);
                                if (newState.smsfAccountingFees !== undefined) setSmsfAccountingFees(newState.smsfAccountingFees);
                                if (newState.smsfConcessionalUsed !== undefined) setSmsfConcessionalUsed(newState.smsfConcessionalUsed);
                                if (newState.smsfNonConcessionalUsed !== undefined) setSmsfNonConcessionalUsed(newState.smsfNonConcessionalUsed);
                                if (newState.yourTbcUsed !== undefined) setYourTbcUsed(newState.yourTbcUsed);
                                if (newState.partnerTbcUsed !== undefined) setPartnerTbcUsed(newState.partnerTbcUsed);
                                if (newState.smsfDirectProperty !== undefined) setSmsfDirectProperty(newState.smsfDirectProperty);
                                if (newState.smsfPropertyValue !== undefined) setSmsfPropertyValue(newState.smsfPropertyValue);
                                if (newState.smsfPropertyLoan !== undefined) setSmsfPropertyLoan(newState.smsfPropertyLoan);
                                if (newState.smsfPropertyInterestRate !== undefined) setSmsfPropertyInterestRate(newState.smsfPropertyInterestRate);
                                if (newState.smsfPropertyLoanTerm !== undefined) setSmsfPropertyLoanTerm(newState.smsfPropertyLoanTerm);
                                if (newState.smsfInHouseAssets !== undefined) setSmsfInHouseAssets(newState.smsfInHouseAssets);
                                if (newState.smsfRelatedPartyLoans !== undefined) setSmsfRelatedPartyLoans(newState.smsfRelatedPartyLoans);
                                if (newState.smsfCommercialProperty !== undefined) setSmsfCommercialProperty(newState.smsfCommercialProperty);
                                if (newState.smsfPropertyRentalYield !== undefined) setSmsfPropertyRentalYield(newState.smsfPropertyRentalYield);
                                if (newState.deathBenefitNominations !== undefined) setDeathBenefitNominations(newState.deathBenefitNominations);
                                if (newState.reversionaryPensionEnabled !== undefined) setReversionaryPensionEnabled(newState.reversionaryPensionEnabled);
                                if (newState.reversionaryPensionAmount !== undefined) setReversionaryPensionAmount(newState.reversionaryPensionAmount);
                                if (newState.spouseAge !== undefined) setSpouseAge(newState.spouseAge);
                                if (newState.beneficiaries !== undefined) setBeneficiaries(newState.beneficiaries);
                                if (newState.taxFreeComponent !== undefined) setTaxFreeComponent(newState.taxFreeComponent);
                                if (newState.taxableComponent !== undefined) setTaxableComponent(newState.taxableComponent);
                            }}
                        />
                    </div>
                );

            case 'enhanced_compliance':
                return (
                    <div className="space-y-6">
                        <SMSFEnhancedComplianceDashboard 
                            smsfState={{
                                smsfData,
                                memberProfile,
                                constraints,
                                smsfTrusteeCount,
                                smsfEstablishedDate,
                                smsfAuditFees,
                                smsfAdminFees,
                                smsfAccountingFees,
                                smsfConcessionalUsed,
                                smsfNonConcessionalUsed,
                                yourTbcUsed,
                                partnerTbcUsed,
                                smsfDirectProperty,
                                smsfPropertyValue,
                                smsfPropertyLoan,
                                smsfPropertyInterestRate,
                                smsfPropertyLoanTerm,
                                smsfInHouseAssets,
                                smsfRelatedPartyLoans,
                                smsfCommercialProperty,
                                smsfPropertyRentalYield,
                                deathBenefitNominations,
                                reversionaryPensionEnabled,
                                reversionaryPensionAmount,
                                spouseAge,
                                beneficiaries,
                                taxFreeComponent,
                                taxableComponent
                            }}
                            setSmsfState={(newState) => {
                                // Update individual state variables based on newState
                                if (newState.smsfData) setSmsfData(newState.smsfData);
                                if (newState.memberProfile) setMemberProfile(newState.memberProfile);
                                if (newState.constraints) setConstraints(newState.constraints);
                                if (newState.smsfTrusteeCount !== undefined) setSmsfTrusteeCount(newState.smsfTrusteeCount);
                                if (newState.smsfEstablishedDate !== undefined) setSmsfEstablishedDate(newState.smsfEstablishedDate);
                                if (newState.smsfAuditFees !== undefined) setSmsfAuditFees(newState.smsfAuditFees);
                                if (newState.smsfAdminFees !== undefined) setSmsfAdminFees(newState.smsfAdminFees);
                                if (newState.smsfAccountingFees !== undefined) setSmsfAccountingFees(newState.smsfAccountingFees);
                                if (newState.smsfConcessionalUsed !== undefined) setSmsfConcessionalUsed(newState.smsfConcessionalUsed);
                                if (newState.smsfNonConcessionalUsed !== undefined) setSmsfNonConcessionalUsed(newState.smsfNonConcessionalUsed);
                                if (newState.yourTbcUsed !== undefined) setYourTbcUsed(newState.yourTbcUsed);
                                if (newState.partnerTbcUsed !== undefined) setPartnerTbcUsed(newState.partnerTbcUsed);
                                if (newState.smsfDirectProperty !== undefined) setSmsfDirectProperty(newState.smsfDirectProperty);
                                if (newState.smsfPropertyValue !== undefined) setSmsfPropertyValue(newState.smsfPropertyValue);
                                if (newState.smsfPropertyLoan !== undefined) setSmsfPropertyLoan(newState.smsfPropertyLoan);
                                if (newState.smsfPropertyInterestRate !== undefined) setSmsfPropertyInterestRate(newState.smsfPropertyInterestRate);
                                if (newState.smsfPropertyLoanTerm !== undefined) setSmsfPropertyLoanTerm(newState.smsfPropertyLoanTerm);
                                if (newState.smsfInHouseAssets !== undefined) setSmsfInHouseAssets(newState.smsfInHouseAssets);
                                if (newState.smsfRelatedPartyLoans !== undefined) setSmsfRelatedPartyLoans(newState.smsfRelatedPartyLoans);
                                if (newState.smsfCommercialProperty !== undefined) setSmsfCommercialProperty(newState.smsfCommercialProperty);
                                if (newState.smsfPropertyRentalYield !== undefined) setSmsfPropertyRentalYield(newState.smsfPropertyRentalYield);
                                if (newState.deathBenefitNominations !== undefined) setDeathBenefitNominations(newState.deathBenefitNominations);
                                if (newState.reversionaryPensionEnabled !== undefined) setReversionaryPensionEnabled(newState.reversionaryPensionEnabled);
                                if (newState.reversionaryPensionAmount !== undefined) setReversionaryPensionAmount(newState.reversionaryPensionAmount);
                                if (newState.spouseAge !== undefined) setSpouseAge(newState.spouseAge);
                                if (newState.beneficiaries !== undefined) setBeneficiaries(newState.beneficiaries);
                                if (newState.taxFreeComponent !== undefined) setTaxFreeComponent(newState.taxFreeComponent);
                                if (newState.taxableComponent !== undefined) setTaxableComponent(newState.taxableComponent);
                            }}
                        />
                    </div>
                );

            case 'administration':
                return (
                    <div className="space-y-6">
                        <SMSFAdministrationManager 
                            smsfState={{
                                smsfData,
                                memberProfile,
                                constraints,
                                smsfTrusteeCount,
                                smsfEstablishedDate,
                                smsfAuditFees,
                                smsfAdminFees,
                                smsfAccountingFees,
                                smsfConcessionalUsed,
                                smsfNonConcessionalUsed,
                                yourTbcUsed,
                                partnerTbcUsed,
                                smsfDirectProperty,
                                smsfPropertyValue,
                                smsfPropertyLoan,
                                smsfPropertyInterestRate,
                                smsfPropertyLoanTerm,
                                smsfInHouseAssets,
                                smsfRelatedPartyLoans,
                                smsfCommercialProperty,
                                smsfPropertyRentalYield,
                                deathBenefitNominations,
                                reversionaryPensionEnabled,
                                reversionaryPensionAmount,
                                spouseAge,
                                beneficiaries,
                                taxFreeComponent,
                                taxableComponent
                            }}
                            setSmsfState={(newState) => {
                                // Update individual state variables based on newState
                                if (newState.smsfData) setSmsfData(newState.smsfData);
                                if (newState.memberProfile) setMemberProfile(newState.memberProfile);
                                if (newState.constraints) setConstraints(newState.constraints);
                                if (newState.smsfTrusteeCount !== undefined) setSmsfTrusteeCount(newState.smsfTrusteeCount);
                                if (newState.smsfEstablishedDate !== undefined) setSmsfEstablishedDate(newState.smsfEstablishedDate);
                                if (newState.smsfAuditFees !== undefined) setSmsfAuditFees(newState.smsfAuditFees);
                                if (newState.smsfAdminFees !== undefined) setSmsfAdminFees(newState.smsfAdminFees);
                                if (newState.smsfAccountingFees !== undefined) setSmsfAccountingFees(newState.smsfAccountingFees);
                                if (newState.smsfConcessionalUsed !== undefined) setSmsfConcessionalUsed(newState.smsfConcessionalUsed);
                                if (newState.smsfNonConcessionalUsed !== undefined) setSmsfNonConcessionalUsed(newState.smsfNonConcessionalUsed);
                                if (newState.yourTbcUsed !== undefined) setYourTbcUsed(newState.yourTbcUsed);
                                if (newState.partnerTbcUsed !== undefined) setPartnerTbcUsed(newState.partnerTbcUsed);
                                if (newState.smsfDirectProperty !== undefined) setSmsfDirectProperty(newState.smsfDirectProperty);
                                if (newState.smsfPropertyValue !== undefined) setSmsfPropertyValue(newState.smsfPropertyValue);
                                if (newState.smsfPropertyLoan !== undefined) setSmsfPropertyLoan(newState.smsfPropertyLoan);
                                if (newState.smsfPropertyInterestRate !== undefined) setSmsfPropertyInterestRate(newState.smsfPropertyInterestRate);
                                if (newState.smsfPropertyLoanTerm !== undefined) setSmsfPropertyLoanTerm(newState.smsfPropertyLoanTerm);
                                if (newState.smsfInHouseAssets !== undefined) setSmsfInHouseAssets(newState.smsfInHouseAssets);
                                if (newState.smsfRelatedPartyLoans !== undefined) setSmsfRelatedPartyLoans(newState.smsfRelatedPartyLoans);
                                if (newState.smsfCommercialProperty !== undefined) setSmsfCommercialProperty(newState.smsfCommercialProperty);
                                if (newState.smsfPropertyRentalYield !== undefined) setSmsfPropertyRentalYield(newState.smsfPropertyRentalYield);
                                if (newState.deathBenefitNominations !== undefined) setDeathBenefitNominations(newState.deathBenefitNominations);
                                if (newState.reversionaryPensionEnabled !== undefined) setReversionaryPensionEnabled(newState.reversionaryPensionEnabled);
                                if (newState.reversionaryPensionAmount !== undefined) setReversionaryPensionAmount(newState.reversionaryPensionAmount);
                                if (newState.spouseAge !== undefined) setSpouseAge(newState.spouseAge);
                                if (newState.beneficiaries !== undefined) setBeneficiaries(newState.beneficiaries);
                                if (newState.taxFreeComponent !== undefined) setTaxFreeComponent(newState.taxFreeComponent);
                                if (newState.taxableComponent !== undefined) setTaxableComponent(newState.taxableComponent);
                            }}
                        />
                    </div>
                );

            case 'analytics':
                return (
                    <div className="space-y-6">
                        <SMSFAdvancedAnalytics 
                            smsfState={{
                                smsfData,
                                memberProfile,
                                constraints,
                                smsfTrusteeCount,
                                smsfEstablishedDate,
                                smsfAuditFees,
                                smsfAdminFees,
                                smsfAccountingFees,
                                smsfConcessionalUsed,
                                smsfNonConcessionalUsed,
                                yourTbcUsed,
                                partnerTbcUsed,
                                smsfDirectProperty,
                                smsfPropertyValue,
                                smsfPropertyLoan,
                                smsfPropertyInterestRate,
                                smsfPropertyLoanTerm,
                                smsfInHouseAssets,
                                smsfRelatedPartyLoans,
                                smsfCommercialProperty,
                                smsfPropertyRentalYield,
                                deathBenefitNominations,
                                reversionaryPensionEnabled,
                                reversionaryPensionAmount,
                                spouseAge,
                                beneficiaries,
                                taxFreeComponent,
                                taxableComponent,
                                assetAllocationTargets,
                                assetAllocationActual,
                                assetValues,
                                assetPerformance
                            }}
                            setSmsfState={(newState) => {
                                // Update individual state variables based on newState
                                if (newState.smsfData) setSmsfData(newState.smsfData);
                                if (newState.memberProfile) setMemberProfile(newState.memberProfile);
                                if (newState.constraints) setConstraints(newState.constraints);
                                if (newState.smsfTrusteeCount !== undefined) setSmsfTrusteeCount(newState.smsfTrusteeCount);
                                if (newState.smsfEstablishedDate !== undefined) setSmsfEstablishedDate(newState.smsfEstablishedDate);
                                if (newState.smsfAuditFees !== undefined) setSmsfAuditFees(newState.smsfAuditFees);
                                if (newState.smsfAdminFees !== undefined) setSmsfAdminFees(newState.smsfAdminFees);
                                if (newState.smsfAccountingFees !== undefined) setSmsfAccountingFees(newState.smsfAccountingFees);
                                if (newState.smsfConcessionalUsed !== undefined) setSmsfConcessionalUsed(newState.smsfConcessionalUsed);
                                if (newState.smsfNonConcessionalUsed !== undefined) setSmsfNonConcessionalUsed(newState.smsfNonConcessionalUsed);
                                if (newState.yourTbcUsed !== undefined) setYourTbcUsed(newState.yourTbcUsed);
                                if (newState.partnerTbcUsed !== undefined) setPartnerTbcUsed(newState.partnerTbcUsed);
                                if (newState.smsfDirectProperty !== undefined) setSmsfDirectProperty(newState.smsfDirectProperty);
                                if (newState.smsfPropertyValue !== undefined) setSmsfPropertyValue(newState.smsfPropertyValue);
                                if (newState.smsfPropertyLoan !== undefined) setSmsfPropertyLoan(newState.smsfPropertyLoan);
                                if (newState.smsfPropertyInterestRate !== undefined) setSmsfPropertyInterestRate(newState.smsfPropertyInterestRate);
                                if (newState.smsfPropertyLoanTerm !== undefined) setSmsfPropertyLoanTerm(newState.smsfPropertyLoanTerm);
                                if (newState.smsfInHouseAssets !== undefined) setSmsfInHouseAssets(newState.smsfInHouseAssets);
                                if (newState.smsfRelatedPartyLoans !== undefined) setSmsfRelatedPartyLoans(newState.smsfRelatedPartyLoans);
                                if (newState.smsfCommercialProperty !== undefined) setSmsfCommercialProperty(newState.smsfCommercialProperty);
                                if (newState.smsfPropertyRentalYield !== undefined) setSmsfPropertyRentalYield(newState.smsfPropertyRentalYield);
                                if (newState.deathBenefitNominations !== undefined) setDeathBenefitNominations(newState.deathBenefitNominations);
                                if (newState.reversionaryPensionEnabled !== undefined) setReversionaryPensionEnabled(newState.reversionaryPensionEnabled);
                                if (newState.reversionaryPensionAmount !== undefined) setReversionaryPensionAmount(newState.reversionaryPensionAmount);
                                if (newState.spouseAge !== undefined) setSpouseAge(newState.spouseAge);
                                if (newState.beneficiaries !== undefined) setBeneficiaries(newState.beneficiaries);
                                if (newState.taxFreeComponent !== undefined) setTaxFreeComponent(newState.taxFreeComponent);
                                if (newState.taxableComponent !== undefined) setTaxableComponent(newState.taxableComponent);
                            }}
                        />
                    </div>
                );

            case 'quick_check':
                return (
                    <div className="space-y-6">
                        <SMSFQuickRetirementCheck 
                            smsfState={{
                                smsfData,
                                memberProfile
                            }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData) setSmsfData(newState.smsfData);
                                if (newState.memberProfile) setMemberProfile(newState.memberProfile);
                            }}
                        />
                    </div>
                );

            case 'insurance':
                return (
                    <div className="space-y-6">
                        <SMSFInsuranceAnalysis 
                            smsfState={{
                                smsfData,
                                memberProfile,
                                constraints,
                                smsfTrusteeCount,
                                smsfEstablishedDate,
                                smsfAuditFees,
                                smsfAdminFees,
                                smsfAccountingFees
                            }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData !== undefined) setSmsfData(newState.smsfData);
                                if (newState.memberProfile !== undefined) setMemberProfile(newState.memberProfile);
                                if (newState.constraints !== undefined) setConstraints(newState.constraints);
                                if (newState.smsfTrusteeCount !== undefined) setSmsfTrusteeCount(newState.smsfTrusteeCount);
                                if (newState.smsfEstablishedDate !== undefined) setSmsfEstablishedDate(newState.smsfEstablishedDate);
                                if (newState.smsfAuditFees !== undefined) setSmsfAuditFees(newState.smsfAuditFees);
                                if (newState.smsfAdminFees !== undefined) setSmsfAdminFees(newState.smsfAdminFees);
                                if (newState.smsfAccountingFees !== undefined) setSmsfAccountingFees(newState.smsfAccountingFees);
                            }}
                        />
                    </div>
                );

            case 'estate':
                return (
                    <div className="space-y-6">
                        <SMSFEstatePlanning 
                            smsfState={{
                                smsfData,
                                memberProfile,
                                constraints,
                                smsfTrusteeCount,
                                smsfEstablishedDate,
                                smsfAuditFees,
                                smsfAdminFees,
                                smsfAccountingFees
                            }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData !== undefined) setSmsfData(newState.smsfData);
                                if (newState.memberProfile !== undefined) setMemberProfile(newState.memberProfile);
                                if (newState.constraints !== undefined) setConstraints(newState.constraints);
                                if (newState.smsfTrusteeCount !== undefined) setSmsfTrusteeCount(newState.smsfTrusteeCount);
                                if (newState.smsfEstablishedDate !== undefined) setSmsfEstablishedDate(newState.smsfEstablishedDate);
                                if (newState.smsfAuditFees !== undefined) setSmsfAuditFees(newState.smsfAuditFees);
                                if (newState.smsfAdminFees !== undefined) setSmsfAdminFees(newState.smsfAdminFees);
                                if (newState.smsfAccountingFees !== undefined) setSmsfAccountingFees(newState.smsfAccountingFees);
                            }}
                        />
                    </div>
                );

            case 'performance':
                return (
                    <div className="space-y-6">
                        <SMSFPerformanceOptimization 
                            smsfState={{
                                smsfData,
                                memberProfile,
                                constraints,
                                smsfTrusteeCount,
                                smsfEstablishedDate,
                                smsfAuditFees,
                                smsfAdminFees,
                                smsfAccountingFees
                            }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData !== undefined) setSmsfData(newState.smsfData);
                                if (newState.memberProfile !== undefined) setMemberProfile(newState.memberProfile);
                                if (newState.constraints !== undefined) setConstraints(newState.constraints);
                                if (newState.smsfTrusteeCount !== undefined) setSmsfTrusteeCount(newState.smsfTrusteeCount);
                                if (newState.smsfEstablishedDate !== undefined) setSmsfEstablishedDate(newState.smsfEstablishedDate);
                                if (newState.smsfAuditFees !== undefined) setSmsfAuditFees(newState.smsfAuditFees);
                                if (newState.smsfAdminFees !== undefined) setSmsfAdminFees(newState.smsfAdminFees);
                                if (newState.smsfAccountingFees !== undefined) setSmsfAccountingFees(newState.smsfAccountingFees);
                            }}
                        />
                    </div>
                );

            case 'lrba':
                return (
                    <div className="space-y-6">
                        <SMSFLRBACalculator 
                            smsfState={{
                                smsfData,
                                memberProfile,
                                constraints,
                                smsfTrusteeCount,
                                smsfEstablishedDate,
                                smsfAuditFees,
                                smsfAdminFees,
                                smsfAccountingFees,
                                smsfDirectProperty,
                                smsfPropertyValue,
                                smsfPropertyLoan,
                                smsfPropertyInterestRate,
                                smsfPropertyLoanTerm,
                                smsfPropertyRentalYield
                            }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData !== undefined) setSmsfData(newState.smsfData);
                                if (newState.memberProfile !== undefined) setMemberProfile(newState.memberProfile);
                                if (newState.constraints !== undefined) setConstraints(newState.constraints);
                                if (newState.smsfTrusteeCount !== undefined) setSmsfTrusteeCount(newState.smsfTrusteeCount);
                                if (newState.smsfEstablishedDate !== undefined) setSmsfEstablishedDate(newState.smsfEstablishedDate);
                                if (newState.smsfAuditFees !== undefined) setSmsfAuditFees(newState.smsfAuditFees);
                                if (newState.smsfAdminFees !== undefined) setSmsfAdminFees(newState.smsfAdminFees);
                                if (newState.smsfAccountingFees !== undefined) setSmsfAccountingFees(newState.smsfAccountingFees);
                                if (newState.smsfDirectProperty !== undefined) setSmsfDirectProperty(newState.smsfDirectProperty);
                                if (newState.smsfPropertyValue !== undefined) setSmsfPropertyValue(newState.smsfPropertyValue);
                                if (newState.smsfPropertyLoan !== undefined) setSmsfPropertyLoan(newState.smsfPropertyLoan);
                                if (newState.smsfPropertyInterestRate !== undefined) setSmsfPropertyInterestRate(newState.smsfPropertyInterestRate);
                                if (newState.smsfPropertyLoanTerm !== undefined) setSmsfPropertyLoanTerm(newState.smsfPropertyLoanTerm);
                                if (newState.smsfPropertyRentalYield !== undefined) setSmsfPropertyRentalYield(newState.smsfPropertyRentalYield);
                            }}
                        />
                    </div>
                );

            case 'division293':
                return (
                    <div className="space-y-6">
                        <SMSFDivision293Calculator 
                            smsfState={{
                                smsfData,
                                memberProfile,
                                constraints,
                                smsfConcessionalUsed,
                                smsfNonConcessionalUsed
                            }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData !== undefined) setSmsfData(newState.smsfData);
                                if (newState.memberProfile !== undefined) setMemberProfile(newState.memberProfile);
                                if (newState.constraints !== undefined) setConstraints(newState.constraints);
                                if (newState.smsfConcessionalUsed !== undefined) setSmsfConcessionalUsed(newState.smsfConcessionalUsed);
                                if (newState.smsfNonConcessionalUsed !== undefined) setSmsfNonConcessionalUsed(newState.smsfNonConcessionalUsed);
                            }}
                        />
                    </div>
                );

            case 'division296':
                return (
                    <div className="space-y-6">
                        <SMSFDivision296Calculator smsfState={smsfState} />
                    </div>
                );

            case 'collectibles':
                return (
                    <div className="space-y-6">
                        <SMSFCollectiblesManager 
                            smsfState={{
                                smsfData,
                                memberProfile,
                                constraints
                            }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData !== undefined) setSmsfData(newState.smsfData);
                                if (newState.memberProfile !== undefined) setMemberProfile(newState.memberProfile);
                                if (newState.constraints !== undefined) setConstraints(newState.constraints);
                            }}
                        />
                    </div>
                );

            case 'related_party':
                return (
                    <div className="space-y-6">
                        <SMSFRelatedPartyMonitor 
                            smsfState={{
                                smsfData,
                                memberProfile,
                                smsfInHouseAssets,
                                smsfRelatedPartyLoans
                            }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData !== undefined) setSmsfData(newState.smsfData);
                                if (newState.memberProfile !== undefined) setMemberProfile(newState.memberProfile);
                                if (newState.smsfInHouseAssets !== undefined) setSmsfInHouseAssets(newState.smsfInHouseAssets);
                                if (newState.smsfRelatedPartyLoans !== undefined) setSmsfRelatedPartyLoans(newState.smsfRelatedPartyLoans);
                            }}
                        />
                    </div>
                );


            case 'tax_harvesting':
                return (
                    <div className="space-y-6">
                        <SMSFTaxLossHarvesting 
                            smsfState={{
                                smsfData,
                                memberProfile,
                                assetValues,
                                assetPerformance
                            }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData !== undefined) setSmsfData(newState.smsfData);
                                if (newState.memberProfile !== undefined) setMemberProfile(newState.memberProfile);
                                if (newState.assetValues !== undefined) setAssetValues(newState.assetValues);
                                if (newState.assetPerformance !== undefined) setAssetPerformance(newState.assetPerformance);
                            }}
                        />
                    </div>
                );

            case 'carry_forward':
                return (
                    <div className="space-y-6">
                        <SMSFContributionCarryForward 
                            smsfState={{
                                smsfData,
                                smsfAdminFees,
                                smsfAccountingFees,
                                smsfConcessionalUsed,
                                smsfNonConcessionalUsed,
                                yourTbcUsed,
                                partnerTbcUsed
                            }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData) setSmsfData(newState.smsfData);
                                if (newState.smsfAdminFees !== undefined) setSmsfAdminFees(newState.smsfAdminFees);
                                if (newState.smsfAccountingFees !== undefined) setSmsfAccountingFees(newState.smsfAccountingFees);
                                if (newState.smsfConcessionalUsed !== undefined) setSmsfConcessionalUsed(newState.smsfConcessionalUsed);
                                if (newState.smsfNonConcessionalUsed !== undefined) setSmsfNonConcessionalUsed(newState.smsfNonConcessionalUsed);
                            }}
                        />
                    </div>
                );

            case 'bring_forward':
                return (
                    <div className="space-y-6">
                        <SMSFBringForwardCalculator 
                            smsfState={{ smsfData, smsfNonConcessionalUsed }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData) setSmsfData(newState.smsfData);
                                if (newState.smsfNonConcessionalUsed !== undefined) setSmsfNonConcessionalUsed(newState.smsfNonConcessionalUsed);
                            }}
                        />
                    </div>
                );

            case 'work_test':
                return (
                    <div className="space-y-6">
                        <SMSFWorkTestTool 
                            smsfState={{ smsfData, memberProfile }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData) setSmsfData(newState.smsfData);
                            }}
                        />
                    </div>
                );

            case 'fhss':
                return (
                    <div className="space-y-6">
                        <SMSFFHSSCalculator 
                            smsfState={{ smsfData }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData) setSmsfData(newState.smsfData);
                            }}
                        />
                    </div>
                );

            case 'spouse_split':
                return (
                    <div className="space-y-6">
                        <SMSFSpouseContributionSplitting 
                            smsfState={{ smsfData }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData) setSmsfData(newState.smsfData);
                            }}
                        />
                    </div>
                );

            case 'limits_monitor':
                return (
                    <div className="space-y-6">
                        <SMSFInvestmentLimitsMonitor 
                            smsfState={{
                                smsfData,
                                smsfPropertyValue,
                                smsfPropertyLoan,
                                smsfInHouseAssets,
                                smsfRelatedPartyLoans
                            }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData) setSmsfData(newState.smsfData);
                                if (newState.smsfPropertyValue !== undefined) setSmsfPropertyValue(newState.smsfPropertyValue);
                                if (newState.smsfPropertyLoan !== undefined) setSmsfPropertyLoan(newState.smsfPropertyLoan);
                                if (newState.smsfInHouseAssets !== undefined) setSmsfInHouseAssets(newState.smsfInHouseAssets);
                                if (newState.smsfRelatedPartyLoans !== undefined) setSmsfRelatedPartyLoans(newState.smsfRelatedPartyLoans);
                            }}
                        />
                    </div>
                );

            case 'co_contribution':
                return (
                    <div className="space-y-6">
                        <SMSFGovernmentCoContribution 
                            smsfState={{
                                smsfData,
                                smsfConcessionalUsed,
                                smsfNonConcessionalUsed
                            }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData) setSmsfData(newState.smsfData);
                                if (newState.smsfConcessionalUsed !== undefined) setSmsfConcessionalUsed(newState.smsfConcessionalUsed);
                                if (newState.smsfNonConcessionalUsed !== undefined) setSmsfNonConcessionalUsed(newState.smsfNonConcessionalUsed);
                            }}
                        />
                    </div>
                );

            case 'downsizer':
                return (
                    <div className="space-y-6">
                        <SMSFDownsizerContribution 
                            smsfState={{
                                smsfData,
                                smsfConcessionalUsed,
                                smsfNonConcessionalUsed
                            }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData) setSmsfData(newState.smsfData);
                                if (newState.smsfConcessionalUsed !== undefined) setSmsfConcessionalUsed(newState.smsfConcessionalUsed);
                                if (newState.smsfNonConcessionalUsed !== undefined) setSmsfNonConcessionalUsed(newState.smsfNonConcessionalUsed);
                            }}
                        />
                    </div>
                );

            case 'succession':
                return (
                    <div className="space-y-6">
                        <SMSFSuccessionPlanning 
                            smsfState={{ smsfData }}
                            setSmsfState={(newState) => {
                                if (newState.smsfData !== undefined) setSmsfData(newState.smsfData);
                            }}
                        />
                    </div>
                );
                
            case 'succession_old':
                return (
                    <div className="space-y-6">
                        {/* Death Benefit Nominations */}
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h3 className="text-lg font-semibold mb-4">Death Benefit Nominations</h3>
                            
                            {/* Super Balance Components */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <InfoTooltip content="The tax-free component of your super balance includes non-concessional contributions and some rollovers. This component is tax-free when paid as a death benefit.">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Tax-Free Component ($)</label>
                                    </InfoTooltip>
                                    <input
                                        {...getNumericInputProps()}
                                        value={taxFreeComponent || ''}
                                        onChange={(e) => setTaxFreeComponent(parseInt(e.target.value) || 0)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Taxable Component ($)</label>
                                    <input
                                        {...getNumericInputProps()}
                                        value={taxableComponent || ''}
                                        onChange={(e) => setTaxableComponent(parseInt(e.target.value) || 0)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            {/* Beneficiaries */}
                            <div>
                                <h4 className="font-semibold mb-2">Beneficiaries</h4>
                                <div className="space-y-3">
                                    {beneficiaries.map((beneficiary, index) => (
                                        <div key={index} className="border border-gray-200 rounded-md p-3">
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                                                    <input
                                                        type="text"
                                                        value={beneficiary.name}
                                                        onChange={(e) => {
                                                            const newBeneficiaries = [...beneficiaries];
                                                            newBeneficiaries[index].name = e.target.value;
                                                            setBeneficiaries(newBeneficiaries);
                                                        }}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Age</label>
                                                    <input
                                                        {...getNumericInputProps()}
                                                        value={beneficiary.age}
                                                        onChange={(e) => {
                                                            const newBeneficiaries = [...beneficiaries];
                                                            newBeneficiaries[index].age = parseInt(e.target.value) || 0;
                                                            setBeneficiaries(newBeneficiaries);
                                                        }}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Relationship</label>
                                                    <select
                                                        value={beneficiary.relationship}
                                                        onChange={(e) => {
                                                            const newBeneficiaries = [...beneficiaries];
                                                            newBeneficiaries[index].relationship = e.target.value;
                                                            newBeneficiaries[index].isDependent = e.target.value === 'spouse';
                                                            setBeneficiaries(newBeneficiaries);
                                                        }}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                                    >
                                                        <option value="spouse">Spouse</option>
                                                        <option value="child">Child</option>
                                                        <option value="dependent">Dependent</option>
                                                        <option value="other">Other</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Percentage (%)</label>
                                                    <input
                                                        {...getNumericInputProps()}
                                                        min="0"
                                                        max="100"
                                                        value={beneficiary.percentage}
                                                        onChange={(e) => {
                                                            const newBeneficiaries = [...beneficiaries];
                                                            newBeneficiaries[index].percentage = parseInt(e.target.value) || 0;
                                                            setBeneficiaries(newBeneficiaries);
                                                        }}
                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Reversionary Pension */}
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold">Reversionary Pension</h3>
                                <input
                                    type="checkbox"
                                    checked={reversionaryPensionEnabled}
                                    onChange={(e) => setReversionaryPensionEnabled(e.target.checked)}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                            </div>
                            
                            {reversionaryPensionEnabled && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Pension Amount ($)</label>
                                        <input
                                            {...getNumericInputProps()}
                                            value={reversionaryPensionAmount || ''}
                                            onChange={(e) => setReversionaryPensionAmount(parseInt(e.target.value) || 0)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Surviving Spouse Age</label>
                                        <input
                                            {...getNumericInputProps()}
                                            value={spouseAge}
                                            onChange={(e) => setSpouseAge(parseInt(e.target.value) || 65)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 'enhanced':
                return (
                    <div className="space-y-6">
                        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-lg border-2 border-yellow-300">
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">📋 Report Generation</h2>
                            <p className="text-gray-700 mb-4">
                                For comprehensive SMSF reports including ATO Annual Returns, Member Statements, Compliance Reports, 
                                and more, please use the <strong>Reports</strong> tab which provides full-featured PDF export capabilities.
                            </p>
                            <button
                                onClick={() => setActiveTab('reports')}
                                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition shadow-md"
                            >
                                → Go to Reports Tab
                            </button>
                        </div>
                        
                        {/* Keep legacy enhanced features for document templates */}
                        <SMSFEnhancedFeatures 
                            smsfData={{
                                ...smsfData,
                                members: getDerivedMembers() // Use comprehensive member data
                            }} 
                        />
                    </div>
                );

            case 'reports':
                return (
                    <div className="space-y-6">
                        <SMSFReportingCenter 
                            smsfState={{
                                smsfData: {
                                    ...smsfData,
                                    members: getDerivedMembers(), // Use comprehensive member data with TFN, DOB, Address, etc.
                                    trustees: (() => {
                                        // Derive trustee data from Configuration
                                        const derivedMembers = getDerivedMembers();
                                        return derivedMembers.map((member, index) => ({
                                            name: smsfData[`trustee${index + 1}Name`] || member.name,
                                            role: smsfData[`trustee${index + 1}Role`] || 'Individual Trustee',
                                            address: member.address,
                                            tfn: member.tfn,
                                            memberDetails: member
                                        }));
                                    })()
                                },
                                memberProfile,
                                constraints,
                                smsfTrusteeCount,
                                smsfEstablishedDate,
                                smsfAuditFees,
                                smsfAdminFees,
                                smsfAccountingFees,
                                smsfConcessionalUsed,
                                smsfNonConcessionalUsed,
                                yourTbcUsed,
                                partnerTbcUsed,
                                smsfDirectProperty,
                                smsfPropertyValue,
                                smsfPropertyLoan,
                                smsfPropertyInterestRate,
                                smsfPropertyLoanTerm,
                                smsfPropertyRentalYield,
                                smsfInHouseAssets,
                                smsfRelatedPartyLoans
                            }}
                        />
                    </div>
                );

            case 'email_reports':
                return (
                    <div className="space-y-6">
                        <SMSFEmailReporting 
                            smsfState={{
                                smsfData: {
                                    ...smsfData,
                                    members: getDerivedMembers() // Use comprehensive member data
                                },
                                memberProfile,
                                smsfAuditFees,
                                smsfAdminFees,
                                smsfAccountingFees,
                                smsfConcessionalUsed,
                                smsfNonConcessionalUsed,
                                yourTbcUsed,
                                partnerTbcUsed,
                                smsfPropertyValue,
                                smsfPropertyLoan,
                                smsfInHouseAssets,
                                smsfRelatedPartyLoans
                            }}
                        />
                    </div>
                );

            case 'history':
                return (
                    <div className="space-y-6">
                        <SMSFHistoryManager 
                            smsfState={{
                                smsfBalance: smsfData?.totalAssets || 0,
                                smsfConcessional: smsfConcessionalUsed,
                                smsfNonConcessional: smsfNonConcessionalUsed,
                                smsfTBC: yourTbcUsed,
                                smsfAge: memberProfile.age
                            }}
                        />
                    </div>
                );

            case 'data':
                return (
                    <div className="space-y-6">
                        <SMSFDataManager 
                            smsfState={{
                                smsfData,
                                memberProfile,
                                constraints,
                                smsfTrusteeCount,
                                smsfEstablishedDate,
                                smsfAuditFees,
                                smsfAdminFees,
                                smsfAccountingFees,
                                smsfConcessionalUsed,
                                smsfNonConcessionalUsed,
                                yourTbcUsed,
                                partnerTbcUsed,
                                smsfDirectProperty,
                                smsfPropertyValue,
                                smsfPropertyLoan,
                                smsfPropertyInterestRate,
                                smsfPropertyLoanTerm,
                                smsfInHouseAssets,
                                smsfRelatedPartyLoans,
                                smsfCommercialProperty,
                                smsfPropertyRentalYield,
                                deathBenefitNominations,
                                reversionaryPensionEnabled,
                                reversionaryPensionAmount,
                                spouseAge,
                                beneficiaries,
                                taxFreeComponent,
                                taxableComponent
                            }}
                            setSmsfState={(newState) => {
                                // Update individual state variables based on newState
                                if (newState.smsfData) setSmsfData(newState.smsfData);
                                if (newState.memberProfile) setMemberProfile(newState.memberProfile);
                                if (newState.constraints) setConstraints(newState.constraints);
                                if (newState.smsfTrusteeCount !== undefined) setSmsfTrusteeCount(newState.smsfTrusteeCount);
                                if (newState.smsfEstablishedDate !== undefined) setSmsfEstablishedDate(newState.smsfEstablishedDate);
                                if (newState.smsfAuditFees !== undefined) setSmsfAuditFees(newState.smsfAuditFees);
                                if (newState.smsfAdminFees !== undefined) setSmsfAdminFees(newState.smsfAdminFees);
                                if (newState.smsfAccountingFees !== undefined) setSmsfAccountingFees(newState.smsfAccountingFees);
                                if (newState.smsfConcessionalUsed !== undefined) setSmsfConcessionalUsed(newState.smsfConcessionalUsed);
                                if (newState.smsfNonConcessionalUsed !== undefined) setSmsfNonConcessionalUsed(newState.smsfNonConcessionalUsed);
                                if (newState.yourTbcUsed !== undefined) setYourTbcUsed(newState.yourTbcUsed);
                                if (newState.partnerTbcUsed !== undefined) setPartnerTbcUsed(newState.partnerTbcUsed);
                                if (newState.smsfDirectProperty !== undefined) setSmsfDirectProperty(newState.smsfDirectProperty);
                                if (newState.smsfPropertyValue !== undefined) setSmsfPropertyValue(newState.smsfPropertyValue);
                                if (newState.smsfPropertyLoan !== undefined) setSmsfPropertyLoan(newState.smsfPropertyLoan);
                                if (newState.smsfPropertyInterestRate !== undefined) setSmsfPropertyInterestRate(newState.smsfPropertyInterestRate);
                                if (newState.smsfPropertyLoanTerm !== undefined) setSmsfPropertyLoanTerm(newState.smsfPropertyLoanTerm);
                                if (newState.smsfInHouseAssets !== undefined) setSmsfInHouseAssets(newState.smsfInHouseAssets);
                                if (newState.smsfRelatedPartyLoans !== undefined) setSmsfRelatedPartyLoans(newState.smsfRelatedPartyLoans);
                                if (newState.smsfCommercialProperty !== undefined) setSmsfCommercialProperty(newState.smsfCommercialProperty);
                                if (newState.smsfPropertyRentalYield !== undefined) setSmsfPropertyRentalYield(newState.smsfPropertyRentalYield);
                                if (newState.deathBenefitNominations !== undefined) setDeathBenefitNominations(newState.deathBenefitNominations);
                                if (newState.reversionaryPensionEnabled !== undefined) setReversionaryPensionEnabled(newState.reversionaryPensionEnabled);
                                if (newState.reversionaryPensionAmount !== undefined) setReversionaryPensionAmount(newState.reversionaryPensionAmount);
                                if (newState.spouseAge !== undefined) setSpouseAge(newState.spouseAge);
                                if (newState.beneficiaries !== undefined) setBeneficiaries(newState.beneficiaries);
                                if (newState.taxFreeComponent !== undefined) setTaxFreeComponent(newState.taxFreeComponent);
                                if (newState.taxableComponent !== undefined) setTaxableComponent(newState.taxableComponent);
                            }}
                        />
                    </div>
                );

            default:
                return <div>Select a tab to view content</div>;
        }
    };

    return (
        <SMSFMobileWrapper tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab}>
            {/* Hidden Asset Valuation Manager - Always mounted to maintain state */}
            <div style={{ display: 'none' }}>
                <SMSFAssetValuationManager 
                    smsfState={{
                        smsfData,
                        memberProfile,
                        constraints
                    }}
                    setSmsfState={(newState) => {
                        // Update assetValuationData whenever Asset Valuation Manager calculates
                        // Only update if values have actually changed to prevent infinite loops
                        if (newState.assetValues || newState.productiveAssets || newState.assetDetails || 
                            newState.residentialPropertyValue !== undefined || newState.commercialPropertyValue !== undefined ||
                            newState.totalLoanAmount !== undefined) {
                            setAssetValuationData(prev => {
                                // Check if anything actually changed
                                const hasChanged = 
                                    JSON.stringify(prev.assetValues) !== JSON.stringify(newState.assetValues) ||
                                    JSON.stringify(prev.productiveAssets) !== JSON.stringify(newState.productiveAssets) ||
                                    prev.residentialPropertyValue !== newState.residentialPropertyValue ||
                                    prev.commercialPropertyValue !== newState.commercialPropertyValue ||
                                    prev.totalLoanAmount !== newState.totalLoanAmount;
                                
                                if (!hasChanged) {
                                    return prev; // No change, return previous state
                                }
                                
                                return {
                                    ...prev,
                                    ...(newState.assetValues && { assetValues: newState.assetValues }),
                                    ...(newState.productiveAssets && { productiveAssets: newState.productiveAssets }),
                                    ...(newState.assetDetails && { assetDetails: newState.assetDetails }),
                                    ...(newState.residentialPropertyValue !== undefined && { residentialPropertyValue: newState.residentialPropertyValue }),
                                    ...(newState.residentialRentalIncome !== undefined && { residentialRentalIncome: newState.residentialRentalIncome }),
                                    ...(newState.residentialPropertyRentalYield !== undefined && { residentialPropertyRentalYield: newState.residentialPropertyRentalYield }),
                                    ...(newState.residentialPropertyCapitalGrowth !== undefined && { residentialPropertyCapitalGrowth: newState.residentialPropertyCapitalGrowth }),
                                    ...(newState.commercialPropertyValue !== undefined && { commercialPropertyValue: newState.commercialPropertyValue }),
                                    ...(newState.commercialRentalIncome !== undefined && { commercialRentalIncome: newState.commercialRentalIncome }),
                                    ...(newState.commercialPropertyRentalYield !== undefined && { commercialPropertyRentalYield: newState.commercialPropertyRentalYield }),
                                    ...(newState.commercialPropertyCapitalGrowth !== undefined && { commercialPropertyCapitalGrowth: newState.commercialPropertyCapitalGrowth }),
                                    ...(newState.totalLoanAmount !== undefined && { totalLoanAmount: newState.totalLoanAmount }),
                                    ...(newState.avgInterestRate !== undefined && { avgInterestRate: newState.avgInterestRate }),
                                    ...(newState.avgLoanTerm !== undefined && { avgLoanTerm: newState.avgLoanTerm })
                                };
                            });
                        }
                    }}
                />
            </div>
            
            {/* Search Bar Modal */}
            {showSearch && (
                <SMSFSearchBar 
                    tabs={tabs}
                    onNavigate={(tabId) => {
                        setActiveTab(tabId);
                        setShowSearch(false);
                    }}
                    onClose={() => setShowSearch(false)}
                />
            )}

            <div className="max-w-7xl mx-auto p-4 md:p-6">
                
                {/* FREE TRIAL BANNER — shown when user has runs remaining */}
                {!hasSMSFAccess && smsfFreeRunsStatus.canRun && (
                    <div className="mb-6 bg-gradient-to-r from-teal-500 to-emerald-600 rounded-lg shadow-md overflow-hidden">
                        <div className="px-6 py-4 text-white flex flex-col sm:flex-row sm:items-center gap-4">
                            <div className="flex-1">
                                <p className="font-bold text-lg">
                                    🎯 Free Trial — {smsfFreeRunsStatus.runsLeft} of {smsfFreeRunsStatus.reason === 'anonymous' ? SubscriptionManager.MAX_SMSF_ANON_RUNS : SubscriptionManager.MAX_SMSF_ACCT_RUNS} free session{smsfFreeRunsStatus.runsLeft !== 1 ? 's' : ''} remaining
                                </p>
                                <p className="text-teal-100 text-sm mt-0.5">
                                    Enter <strong>your own SMSF data</strong> — fund balance, members, contributions — to see your real compliance status and projections.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    if (typeof window !== 'undefined' && window.gtag) {
                                        window.gtag('event', 'smsf_free_runs_banner_upgrade', { event_category: 'conversion', event_label: 'SMSF free runs banner', value: 1, calculator_tier: 'smsf' });
                                    }
                                    window.dispatchEvent(new CustomEvent('showPricingModal', {
                                        detail: { source: 'smsf_gate', calculatorTier: 'smsf' }
                                    }));
                                }}
                                className="shrink-0 px-5 py-2 bg-white text-teal-700 font-bold rounded-lg hover:bg-gray-100 transition shadow text-sm whitespace-nowrap"
                            >
                                Unlock unlimited — $39.99/mo
                            </button>
                        </div>
                    </div>
                )}

                {/* EMAIL GATE AT TOP — when 1 anon session used, show form so user can unlock 1 more */}
                {!hasSMSFAccess && !smsfFreeRunsStatus.canRun && smsfFreeRunsStatus.reason === 'needs_email' && (
                    <SMSFFreeRunsEmailGate
                        onEmailCaptured={(email) => {
                            SubscriptionManager.setSMSFFreeEmail(email);
                            const newStatus = SubscriptionManager.canRunSMSFFree();
                            setSmsfFreeRunsStatus(newStatus);
                            setShowSmsfEmailGate(false);
                            setIsDemoMode(false);
                            smsfRunRecordedRef.current = false;
                            if (typeof window !== 'undefined' && window.gtag) {
                                window.gtag('event', 'smsf_email_gate_captured', { event_category: 'free_trial', event_label: 'SMSF email gate', value: 1, calculator_tier: 'smsf' });
                            }
                        }}
                        onUpgradeClick={() => {
                            setShowSmsfEmailGate(false);
                            if (typeof window !== 'undefined' && window.gtag) {
                                window.gtag('event', 'smsf_email_gate_upgrade_click', { event_category: 'conversion', event_label: 'SMSF email gate upgrade', value: 1, calculator_tier: 'smsf' });
                            }
                            window.dispatchEvent(new CustomEvent('showPricingModal', {
                                detail: { source: 'smsf_gate', calculatorTier: 'smsf' }
                            }));
                        }}
                        onDismiss={() => setShowSmsfEmailGate(false)}
                    />
                )}

                {/* DEMO BANNER — when all free sessions (1 anon + 1 after email) exhausted, subscribe only */}
                {!hasSMSFAccess && !smsfFreeRunsStatus.canRun && smsfFreeRunsStatus.reason === 'needs_upgrade' && (
                    <div className="mb-6 bg-gradient-to-r from-orange-600 to-red-600 rounded-lg shadow-lg overflow-hidden">
                        <div className="p-6 text-white">
                            <div className="flex items-start gap-4">
                                <div className="text-4xl">🔒</div>
                                <div className="flex-1">
                                    <h3 className="text-2xl font-bold mb-2">You've used your free sessions — unlock the full SMSF suite</h3>
                                    <p className="text-orange-100 mb-4">Your free trial is up. Subscribe for unlimited access to all 36+ SMSF tools.</p>
                                    
                                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4">
                                        <p className="font-semibold mb-3 text-lg">All Access delivers deep SMSF analysis at a trustee-friendly price point:</p>
                                        <ul className="space-y-2 text-sm">
                                            <li className="flex items-start gap-2">
                                                <span className="text-yellow-300 font-bold">✓</span>
                                                <span>Real-time compliance monitoring (TBC, contribution caps, in-house assets, sole purpose test)</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-yellow-300 font-bold">✓</span>
                                                <span>LRBA calculator (property borrowing limits, repayment schedules, compliance checks)</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-yellow-300 font-bold">✓</span>
                                                <span>Division 293 &amp; Division 296 tax, contribution bring-forward, catch-up carry-forward planning</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-yellow-300 font-bold">✓</span>
                                                <span>Estate planning (binding death benefit nominations, reversionary pensions)</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <span className="text-yellow-300 font-bold">✓</span>
                                                <span>Fee analysis vs retail/industry funds + investment strategy builder</span>
                                            </li>
                                        </ul>
                                    </div>
                                    
                                    <div className="flex flex-wrap gap-3">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (typeof window !== 'undefined' && window.gtag) {
                                                    window.gtag('event', 'demo_banner_cta_click', { event_category: 'conversion', event_label: 'Upgrade CTA Monthly - SMSF', value: 1, calculator_tier: 'smsf' });
                                                }
                                                startRecommendedCheckout({
                                                    tier: 'premium',
                                                    billing: 'monthly',
                                                    source: 'smsf_gate',
                                                    calculatorTier: 'smsf',
                                                    surface: 'smsf_free_runs_exhausted_monthly',
                                                }).catch(alertCheckoutFailure);
                                            }}
                                            className="px-6 py-3 bg-white text-orange-600 font-bold rounded-lg hover:bg-gray-100 transition shadow-lg"
                                        >
                                            Start monthly — $39.99
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (typeof window !== 'undefined' && window.gtag) {
                                                    window.gtag('event', 'demo_banner_cta_click', { event_category: 'conversion', event_label: 'Upgrade CTA Annual - SMSF', value: 1, calculator_tier: 'smsf' });
                                                }
                                                startRecommendedCheckout({
                                                    tier: 'premium',
                                                    billing: 'annual',
                                                    source: 'smsf_gate',
                                                    calculatorTier: 'smsf',
                                                    surface: 'smsf_free_runs_exhausted',
                                                }).catch(alertCheckoutFailure);
                                            }}
                                            className="px-6 py-3 bg-white text-orange-600 font-bold rounded-lg hover:bg-gray-100 transition shadow-lg"
                                        >
                                            Start annual — $399 (save $80)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (typeof window !== 'undefined' && window.gtag) {
                                                    window.gtag('event', 'demo_banner_cta_click', { event_category: 'conversion', event_label: 'See Pricing - SMSF', value: 1, calculator_tier: 'smsf' });
                                                }
                                                window.dispatchEvent(new CustomEvent('showPricingModal', {
                                                    detail: { source: 'smsf_gate', calculatorTier: 'smsf' }
                                                }));
                                            }}
                                            className="px-6 py-3 bg-white/20 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/30 transition border-2 border-white/50"
                                        >
                                            See Full Pricing
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Header with Search Button */}
                <div className="mb-4 md:mb-8 hidden md:block">
                    <div className="flex justify-between items-start">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">SMSF Calculator</h1>
                            <p className="text-gray-600">Comprehensive Self-Managed Superannuation Fund analysis and planning</p>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    console.log('SMSF PDF button clicked!');
                                    // Import PDFExportManager
                                    import('./PDFExportManager.js').then(module => {
                                        console.log('PDFExportManager loaded:', module);
                                        const PDFExportManager = module.default || module.PDFExportManager;
                                        console.log('Calling exportComprehensiveSMSF with data...');
                                        try {
                                            PDFExportManager.exportComprehensiveSMSF({
                                                smsfData,
                                                memberProfile,
                                                constraints,
                                                smsfTrusteeCount,
                                                smsfEstablishedDate,
                                                smsfAuditFees,
                                                smsfAdminFees,
                                                smsfAccountingFees,
                                                smsfConcessionalUsed,
                                                smsfNonConcessionalUsed,
                                                smsfDirectProperty,
                                                smsfPropertyValue,
                                                smsfPropertyLoan,
                                                smsfPropertyInterestRate,
                                                smsfPropertyLoanTerm,
                                                smsfPropertyRentalYield,
                                                smsfInHouseAssets,
                                                smsfRelatedPartyLoans,
                                                deathBenefitNominations,
                                                reversionaryPensionEnabled,
                                                reversionaryPensionAmount,
                                                beneficiaries,
                                                taxFreeComponent,
                                                taxableComponent,
                                                collectibles: {
                                                    hasCollectibles: smsfData?.hasCollectibles || false,
                                                    items: [
                                                        { type: 'Vintage Vehicles', description: '1969 Ford Mustang', value: 120000, valuationDate: '2024-06-30', insured: true },
                                                        { type: 'Artwork', description: 'Australian Contemporary Art Collection', value: 85000, valuationDate: '2024-08-15', insured: true },
                                                        { type: 'Wine Collection', description: 'Premium Australian Wines', value: 45000, valuationDate: '2024-05-20', insured: true }
                                                    ]
                                                },
                                                complianceStatus: 'Compliant'
                                            });
                                            console.log('PDF generation completed successfully!');
                                        } catch (pdfError) {
                                            console.error('Error during PDF generation:', pdfError);
                                            alert('PDF generation failed: ' + pdfError.message);
                                        }
                                    }).catch(err => {
                                        console.error('Error loading PDF export:', err);
                                        alert('Error loading PDF module. Please try again.');
                                    });
                                }}
                                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition shadow font-semibold"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span>Download PDF</span>
                            </button>
                            <button
                                onClick={() => setShowTutorial(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow"
                            >
                                <span>🎓</span>
                                <span>Tutorial</span>
                            </button>
                            <button
                                onClick={() => setShowSearch(true)}
                                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow"
                            >
                                <span>🔍</span>
                                <span>Search</span>
                                <kbd className="text-xs bg-blue-700 px-2 py-1 rounded">Ctrl+K</kbd>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="mb-4 bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-700 mb-2">Quick start your first action:</p>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => applySmsfQuickStart('setup')}
                            className="px-3 py-1.5 rounded-full text-sm border bg-white text-gray-700 border-gray-300 hover:border-teal-400"
                        >
                            Setup Fund
                        </button>
                        <button
                            type="button"
                            onClick={() => applySmsfQuickStart('contribution')}
                            className="px-3 py-1.5 rounded-full text-sm border bg-white text-gray-700 border-gray-300 hover:border-teal-400"
                        >
                            Contribution Strategy
                        </button>
                        <button
                            type="button"
                            onClick={() => applySmsfQuickStart('pension')}
                            className="px-3 py-1.5 rounded-full text-sm border bg-white text-gray-700 border-gray-300 hover:border-teal-400"
                        >
                            Pension & Compliance
                        </button>
                    </div>
                </div>

                {/* Email gate — shown when anon run exhausted, grants 1 more free session */}
                {showSmsfEmailGate && !hasSMSFAccess && (
                    <SMSFFreeRunsEmailGate
                        onEmailCaptured={(email) => {
                            SubscriptionManager.setSMSFFreeEmail(email);
                            const newStatus = SubscriptionManager.canRunSMSFFree();
                            setSmsfFreeRunsStatus(newStatus);
                            setShowSmsfEmailGate(false);
                            setIsDemoMode(false);
                            smsfRunRecordedRef.current = false; // allow next interaction to record the new run
                            if (typeof window !== 'undefined' && window.gtag) {
                                window.gtag('event', 'smsf_email_gate_captured', { event_category: 'free_trial', event_label: 'SMSF email gate', value: 1, calculator_tier: 'smsf' });
                            }
                        }}
                        onUpgradeClick={() => {
                            setShowSmsfEmailGate(false);
                            if (typeof window !== 'undefined' && window.gtag) {
                                window.gtag('event', 'smsf_email_gate_upgrade_click', { event_category: 'conversion', event_label: 'SMSF email gate upgrade', value: 1, calculator_tier: 'smsf' });
                            }
                            window.dispatchEvent(new CustomEvent('showPricingModal', {
                                detail: { source: 'smsf_gate', calculatorTier: 'smsf' }
                            }));
                        }}
                        onDismiss={() => setShowSmsfEmailGate(false)}
                    />
                )}

                {/* Post-session upsell — shown after first data entry, at moment of engagement */}
                {showSmsfPostUpsell && !hasSMSFAccess && (
                    <div className="mb-6 rounded-xl overflow-hidden shadow-lg border-2 border-orange-200">
                        <div className="bg-gradient-to-r from-orange-500 to-red-500 px-6 py-4 text-white">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                    <p className="text-lg font-bold mb-1">
                                        You're now seeing your real SMSF numbers.
                                    </p>
                                    <p className="text-orange-100 text-sm">
                                        {smsfFreeRunsStatus.canRun
                                            ? `You have ${smsfFreeRunsStatus.runsLeft} free session${smsfFreeRunsStatus.runsLeft !== 1 ? 's' : ''} remaining. Explore all 36+ tools, then subscribe to keep your data and run unlimited scenarios. Your free sessions don't expire; come back anytime.`
                                            : 'Your free session is up. Subscribe to save this scenario and run unlimited what-ifs.'}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowSmsfPostUpsell(false)}
                                    className="text-white/70 hover:text-white text-xl leading-none shrink-0 mt-0.5"
                                    aria-label="Dismiss"
                                >×</button>
                            </div>
                        </div>
                        <div className="bg-white px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                            <div className="flex-1 text-sm text-gray-700">
                                <strong>All Access includes:</strong> compliance dashboard · contribution caps · LRBA · Division 293 · Division 296 · estate planning · pension calculations · fee analysis
                            </div>
                            <div className="flex gap-3 shrink-0">
                                {smsfFreeRunsStatus.canRun ? (
                                    <button
                                        type="button"
                                        onClick={() => setShowSmsfPostUpsell(false)}
                                        className="px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition text-sm"
                                    >
                                        Keep exploring
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowSmsfPostUpsell(false);
                                            setShowSmsfEmailGate(true);
                                        }}
                                        className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg transition text-sm whitespace-nowrap"
                                    >
                                        Get 1 more free session
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (typeof window !== 'undefined' && window.gtag) {
                                            window.gtag('event', 'smsf_post_upsell_upgrade_click', { event_category: 'conversion', event_label: 'SMSF post-session upsell monthly', value: 1, calculator_tier: 'smsf' });
                                        }
                                        if (!smsfFreeRunsStatus.canRun && smsfFreeRunsStatus.reason === 'needs_upgrade') {
                                            startRecommendedCheckout({
                                                tier: 'premium',
                                                billing: 'monthly',
                                                source: 'smsf_gate',
                                                calculatorTier: 'smsf',
                                                surface: 'smsf_free_runs_exhausted_post_session_monthly',
                                            }).catch(alertCheckoutFailure);
                                            return;
                                        }
                                        window.dispatchEvent(new CustomEvent('showPricingModal', {
                                            detail: { source: 'smsf_gate', calculatorTier: 'smsf' }
                                        }));
                                    }}
                                    className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg transition shadow text-sm whitespace-nowrap"
                                >
                                    {!smsfFreeRunsStatus.canRun && smsfFreeRunsStatus.reason === 'needs_upgrade'
                                        ? 'Start monthly — $39.99'
                                        : 'Upgrade — from $39.99/mo'}
                                </button>
                                {!smsfFreeRunsStatus.canRun && smsfFreeRunsStatus.reason === 'needs_upgrade' && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (typeof window !== 'undefined' && window.gtag) {
                                                window.gtag('event', 'smsf_post_upsell_upgrade_click', { event_category: 'conversion', event_label: 'SMSF post-session upsell annual', value: 1, calculator_tier: 'smsf' });
                                            }
                                            startRecommendedCheckout({
                                                tier: 'premium',
                                                billing: 'annual',
                                                source: 'smsf_gate',
                                                calculatorTier: 'smsf',
                                                surface: 'smsf_free_runs_exhausted_post_session',
                                            }).catch(alertCheckoutFailure);
                                        }}
                                        className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-lg transition shadow text-sm whitespace-nowrap"
                                    >
                                        Start annual — $399 (save $80)
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}


                {/* Categorized Navigation */}
                {renderCategorizedNavigation()}

                {/* Validation Panel - Responsive (hidden on SMSF vs Industry so comparison isn't cluttered by main-fund validation) */}
                {activeTab !== 'tutorial' && activeTab !== 'comparison' && (
                    <div className="px-2 md:px-0">
                        <LightweightErrorBoundary message="Error loading validation panel">
                            <SMSFValidationPanel 
                            smsfState={{
                                smsfData,
                                memberProfile,
                                constraints,
                                smsfTrusteeCount,
                                smsfAuditFees,
                                smsfAdminFees,
                                smsfAccountingFees,
                                smsfConcessionalUsed,
                                smsfNonConcessionalUsed
                            }}
                            onNavigateToSection={setActiveTab}
                        />
                        </LightweightErrorBoundary>
                    </div>
                )}

                {/* Tab Content - Mobile Optimized */}
                <div className="min-h-screen md:min-h-96 pb-20 md:pb-0">
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                        {/* Main Content */}
                        <div ref={contentRef} className="lg:col-span-3">
                            <LightweightErrorBoundary message={`Error loading ${activeTab} tab`} onRetry>
                                {renderTabContent()}
                            </LightweightErrorBoundary>
                        </div>

                        {/* Related Calculators Sidebar */}
                        <div className="lg:col-span-1 hidden lg:block">
                            <div className="sticky top-4">
                                <SMSFRelatedCalculators 
                                    currentCalculator={activeTab}
                                    onNavigate={setActiveTab}
                                    smsfState={{
                                        smsfData,
                                        smsfBalance: smsfData?.totalAssets || 0,
                                        smsfConcessionalUsed,
                                        smsfNonConcessionalUsed
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Email capture - SMSF insights & checklist */}
            <div className="max-w-4xl mx-auto px-4 py-8 border-t border-gray-200 bg-gray-50">
                <EmailCaptureForm calculatorType="smsf" results={null} inputs={null} getPdfBase64={null} />
            </div>

            {/* Tutorial – draggable panel (same as Advanced) */}
            {showTutorial && (
                <DraggableTutorialPanel onClose={() => setShowTutorial(false)} title="SMSF Calculator Tutorial">
                    <SMSFTutorialSystem
                        renderInPanel
                        smsfState={{
                            smsfData,
                            memberProfile,
                            constraints,
                            smsfTrusteeCount,
                            smsfEstablishedDate,
                            smsfAuditFees,
                            smsfAdminFees,
                            smsfAccountingFees,
                            smsfConcessionalUsed,
                            smsfNonConcessionalUsed,
                            yourTbcUsed,
                            partnerTbcUsed,
                            smsfDirectProperty,
                            smsfPropertyValue,
                            smsfPropertyLoan,
                            smsfPropertyInterestRate,
                            smsfPropertyLoanTerm,
                            smsfInHouseAssets,
                            smsfRelatedPartyLoans,
                            smsfCommercialProperty,
                            smsfPropertyRentalYield,
                            deathBenefitNominations,
                            reversionaryPensionEnabled,
                            reversionaryPensionAmount,
                            spouseAge,
                            beneficiaries,
                            taxFreeComponent,
                            taxableComponent
                        }}
                        setSmsfState={(newState) => {
                            if (newState.smsfData) setSmsfData(newState.smsfData);
                            if (newState.memberProfile) setMemberProfile(newState.memberProfile);
                            if (newState.constraints) setConstraints(newState.constraints);
                            if (newState.smsfTrusteeCount !== undefined) setSmsfTrusteeCount(newState.smsfTrusteeCount);
                            if (newState.smsfEstablishedDate !== undefined) setSmsfEstablishedDate(newState.smsfEstablishedDate);
                            if (newState.smsfAuditFees !== undefined) setSmsfAuditFees(newState.smsfAuditFees);
                            if (newState.smsfAdminFees !== undefined) setSmsfAdminFees(newState.smsfAdminFees);
                            if (newState.smsfAccountingFees !== undefined) setSmsfAccountingFees(newState.smsfAccountingFees);
                            if (newState.smsfConcessionalUsed !== undefined) setSmsfConcessionalUsed(newState.smsfConcessionalUsed);
                            if (newState.smsfNonConcessionalUsed !== undefined) setSmsfNonConcessionalUsed(newState.smsfNonConcessionalUsed);
                            if (newState.yourTbcUsed !== undefined) setYourTbcUsed(newState.yourTbcUsed);
                            if (newState.partnerTbcUsed !== undefined) setPartnerTbcUsed(newState.partnerTbcUsed);
                            if (newState.smsfDirectProperty !== undefined) setSmsfDirectProperty(newState.smsfDirectProperty);
                            if (newState.smsfPropertyValue !== undefined) setSmsfPropertyValue(newState.smsfPropertyValue);
                            if (newState.smsfPropertyLoan !== undefined) setSmsfPropertyLoan(newState.smsfPropertyLoan);
                            if (newState.smsfPropertyInterestRate !== undefined) setSmsfPropertyInterestRate(newState.smsfPropertyInterestRate);
                            if (newState.smsfPropertyLoanTerm !== undefined) setSmsfPropertyLoanTerm(newState.smsfPropertyLoanTerm);
                            if (newState.smsfInHouseAssets !== undefined) setSmsfInHouseAssets(newState.smsfInHouseAssets);
                            if (newState.smsfRelatedPartyLoans !== undefined) setSmsfRelatedPartyLoans(newState.smsfRelatedPartyLoans);
                            if (newState.smsfCommercialProperty !== undefined) setSmsfCommercialProperty(newState.smsfCommercialProperty);
                            if (newState.smsfPropertyRentalYield !== undefined) setSmsfPropertyRentalYield(newState.smsfPropertyRentalYield);
                            if (newState.deathBenefitNominations !== undefined) setDeathBenefitNominations(newState.deathBenefitNominations);
                            if (newState.reversionaryPensionEnabled !== undefined) setReversionaryPensionEnabled(newState.reversionaryPensionEnabled);
                            if (newState.reversionaryPensionAmount !== undefined) setReversionaryPensionAmount(newState.reversionaryPensionAmount);
                            if (newState.spouseAge !== undefined) setSpouseAge(newState.spouseAge);
                            if (newState.beneficiaries !== undefined) setBeneficiaries(newState.beneficiaries);
                            if (newState.taxFreeComponent !== undefined) setTaxFreeComponent(newState.taxFreeComponent);
                            if (newState.taxableComponent !== undefined) setTaxableComponent(newState.taxableComponent);
                        }}
                        onClose={() => setShowTutorial(false)}
                    />
                </DraggableTutorialPanel>
            )}

            {/* Asset Allocation Modal */}
            <SMSFAssetAllocationModal
                isOpen={showAllocationModal}
                onClose={() => setShowAllocationModal(false)}
                members={getDerivedMembers()} // Use derived members from Configuration
                assetBreakdown={assetBreakdownForAllocation}
                onApplyAllocation={(allocationData, mode) => {
                    // Update Configuration fields with allocated balances and asset allocation data
                    const updatedData = { ...smsfData };
                    
                    allocationData.forEach((allocation, index) => {
                        if (allocation) {
                            const memberNum = index + 1;
                            const prefix = `member${memberNum}`;
                            
                            // Update balance in Configuration
                            updatedData[`${prefix}Balance`] = allocation.total;
                            
                            // Store asset allocation temporarily in smsfData.members for transfer logic
                            if (!updatedData.members) updatedData.members = [];
                            const memberName = updatedData[`${prefix}Name`];
                            const existingMemberIndex = updatedData.members.findIndex(m => m.name === memberName);
                            
                            const memberAllocation = {
                                name: memberName,
                                assetAllocation: {
                                    productiveAssets: allocation.productiveAssets,
                                    nonProductiveAssets: allocation.nonProductiveAssets,
                                    percentage: parseFloat(allocation.percentage),
                                    allocationMode: mode
                                }
                            };
                            
                            if (existingMemberIndex >= 0) {
                                updatedData.members[existingMemberIndex] = {
                                    ...updatedData.members[existingMemberIndex],
                                    ...memberAllocation
                                };
                            } else {
                                updatedData.members.push(memberAllocation);
                            }
                        }
                    });
                    
                    setSmsfData(updatedData);
                    setShowAllocationModal(false);
                }}
            />
        </SMSFMobileWrapper>
    );
};

// SMSF Administration Interface
export const SMSFAdministrationInterface = ({ smsfData }) => {
    const [administrationResults, setAdministrationResults] = useState(null);
    const [activeAdminTab, setActiveAdminTab] = useState('trustee');

    useEffect(() => {
        if (smsfData) {
            // REAL administration calculations using SMSFAdministrationModule
            const trusteeChecklist = SMSFAdministrationModule.TrusteeResponsibilitiesManager.getTrusteeResponsibilitiesChecklist();
            const complianceResults = SMSFAdministrationModule.TrusteeResponsibilitiesManager.validateTrusteeCompliance(
                smsfData.completedResponsibilities || []
            );
            
            const recordKeepingValidation = SMSFAdministrationModule.RecordKeepingManager.validateRecordKeeping({
                financialRecords: smsfData.financialRecords || [],
                investmentRecords: smsfData.investmentRecords || [],
                memberRecords: smsfData.memberRecords || [],
                trusteeRecords: smsfData.trusteeRecords || [],
                taxRecords: smsfData.taxRecords || []
            });
            
            const annualReturnData = SMSFAdministrationModule.AnnualReturnManager.prepareAnnualReturn({
                fundDetails: {
                    fundName: smsfData.fundName,
                    abn: smsfData.abn,
                    tfn: smsfData.tfn,
                    financialYear: '2024-25'
                },
                financialData: {
                    totalAssets: smsfData.balance || smsfData.totalAssets || 0,
                    totalLiabilities: smsfData.totalLiabilities || 0,
                    totalIncome: smsfData.totalIncome || 0,
                    totalExpenses: smsfData.totalExpenses || 0
                },
                members: getDerivedMembers(), // Use derived members from Configuration
                contributions: smsfData.contributions || [],
                benefits: smsfData.benefits || []
            });
            
            const memberStatements = getDerivedMembers().map((member, index) => {
                const statement = SMSFAdministrationModule.MemberStatementManager.generateMemberStatement({
                    memberDetails: {
                        memberName: member.name || `Member ${index + 1}`,
                        memberTFN: member.tfn || '',
                        dateOfBirth: member.dateOfBirth || ''
                    },
                    accountDetails: {
                        openingBalance: member.openingBalance || (smsfData.balance || 0) / (getDerivedMembers().length || 2),
                        closingBalance: member.closingBalance || (smsfData.balance || 0) / (getDerivedMembers().length || 2),
                        contributions: member.contributions || [],
                        withdrawals: member.withdrawals || [],
                        earnings: member.earnings || 0,
                        fees: member.fees || 0
                    },
                    taxDetails: member.taxDetails || {},
                    beneficiaries: member.beneficiaries || []
                });
                return statement;
            });
            
            setAdministrationResults({
                trusteeCompliance: complianceResults,
                recordKeeping: recordKeepingValidation,
                annualReturn: annualReturnData,
                memberStatements
            });
        }
    }, [smsfData]);

    const adminTabs = [
        { id: 'trustee', label: 'Trustee Compliance', icon: '👥' },
        { id: 'records', label: 'Record Keeping', icon: '📁' },
        { id: 'returns', label: 'Annual Returns', icon: '📊' },
        { id: 'statements', label: 'Member Statements', icon: '📄' }
    ];

    const renderAdminContent = () => {
        if (!administrationResults) {
            return <div className="text-gray-500">Loading administration data...</div>;
        }

        switch (activeAdminTab) {
            case 'trustee':
                return (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-center">
                                    <div className={`text-2xl font-bold ${administrationResults.trusteeCompliance.overallCompliant ? 'text-green-600' : 'text-red-600'}`}>
                                        {administrationResults.trusteeCompliance.overallCompliant ? '✓' : '✗'}
                                    </div>
                                    <p className="text-sm text-gray-600 mt-1">Overall Compliance</p>
                                </div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-blue-600">{administrationResults.trusteeCompliance.complianceScore}%</div>
                                    <p className="text-sm text-gray-600 mt-1">Compliance Score</p>
                                </div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-orange-600">{administrationResults.trusteeCompliance.pendingResponsibilities.length}</div>
                                    <p className="text-sm text-gray-600 mt-1">Pending Issues</p>
                                </div>
                            </div>
                        </div>
                        
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h4 className="font-medium text-gray-700 mb-4">Trustee Responsibilities</h4>
                            <div className="space-y-3">
                                {administrationResults.trusteeCompliance.completedResponsibilities.map((resp, index) => (
                                    <div key={index} className="flex items-center">
                                        <span className="text-green-600 mr-2">✓</span>
                                        <span className="text-sm">{resp.responsibility}</span>
                                    </div>
                                ))}
                                {administrationResults.trusteeCompliance.pendingResponsibilities.map((resp, index) => (
                                    <div key={index} className="flex items-center">
                                        <span className="text-red-600 mr-2">✗</span>
                                        <span className="text-sm">{resp.responsibility}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );

            case 'records':
                return (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-center">
                                    <div className={`text-2xl font-bold ${administrationResults.recordKeeping.overallCompliant ? 'text-green-600' : 'text-red-600'}`}>
                                        {administrationResults.recordKeeping.overallCompliant ? '✓' : '✗'}
                                    </div>
                                    <p className="text-sm text-gray-600 mt-1">Record Keeping</p>
                                </div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-blue-600">{administrationResults.recordKeeping.missingRecords.length}</div>
                                    <p className="text-sm text-gray-600 mt-1">Missing Records</p>
                                </div>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                                <div className="text-center">
                                    <div className="text-2xl font-bold text-orange-600">{administrationResults.recordKeeping.incompleteRecords.length}</div>
                                    <p className="text-sm text-gray-600 mt-1">Incomplete Records</p>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'returns':
                return (
                    <div className="space-y-4">
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h4 className="font-medium text-gray-700 mb-4">Annual Return Status</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-gray-600">Financial Year</p>
                                    <p className="font-medium">{administrationResults.annualReturn.financialSummary.financialYear}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Validation Status</p>
                                    <p className={`font-medium ${administrationResults.annualReturn.validation.valid ? 'text-green-600' : 'text-red-600'}`}>
                                        {administrationResults.annualReturn.validation.valid ? 'Valid' : 'Invalid'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Total Assets</p>
                                    <p className="font-medium">${administrationResults.annualReturn.financialSummary.totalAssets.toLocaleString()}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600">Completeness</p>
                                    <p className="font-medium">{administrationResults.annualReturn.validation.completeness}%</p>
                                </div>
                            </div>
                        </div>
                    </div>
                );

            case 'statements':
                return (
                    <div className="space-y-4">
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h4 className="font-medium text-gray-700 mb-4">Member Statements</h4>
                            <div className="space-y-4">
                                {administrationResults.memberStatements.map((statement, index) => (
                                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                                        <h5 className="font-medium text-gray-700 mb-2">{statement.memberDetails.memberName}</h5>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <p className="text-sm text-gray-600">Opening Balance</p>
                                                <p className="font-medium">${statement.accountSummary.openingBalance.toLocaleString()}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-600">Closing Balance</p>
                                                <p className="font-medium">${statement.accountSummary.closingBalance.toLocaleString()}</p>
                                            </div>
                                            <div>
                                                <p className="text-sm text-gray-600">Net Change</p>
                                                <p className={`font-medium ${statement.accountSummary.netChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                    ${statement.accountSummary.netChange.toLocaleString()}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );

            default:
                return <div>Select an administration area</div>;
        }
    };

    return (
        <div>
            {/* Administration Tab Navigation */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                    {adminTabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveAdminTab(tab.id)}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeAdminTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <span className="mr-2">{tab.icon}</span>
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Administration Content */}
            <div className="min-h-96">
                {renderAdminContent()}
            </div>
        </div>
    );
};

// SMSF Navigation Component for Integration
export const SMSFNavigation = ({ currentView, onViewChange }) => {
    const smsfViews = [
        { id: 'smsf-calculator', label: 'SMSF Calculator', icon: '🏦' },
        { id: 'basic-calculator', label: 'Basic Calculator', icon: '📊' },
        { id: 'advanced-calculator', label: 'Advanced Calculator', icon: '🎯' },
        { id: 'accumulation-calculator', label: 'Accumulation Calculator', icon: '📈' }
    ];

    return (
        <div className="bg-white shadow-sm border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between h-16">
                    <div className="flex">
                        <div className="flex-shrink-0 flex items-center">
                            <h1 className="text-xl font-bold text-gray-900">Superannuation Calculator</h1>
                        </div>
                        <nav className="hidden sm:ml-6 sm:flex sm:space-x-8">
                            {smsfViews.map((view) => (
                                <button
                                    key={view.id}
                                    onClick={() => onViewChange(view.id)}
                                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                                        currentView === view.id
                                            ? 'border-blue-500 text-gray-900'
                                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                                    }`}
                                >
                                    <span className="mr-2">{view.icon}</span>
                                    {view.label}
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Main SMSF Calculator Interface wrapped with Context Provider and Error Boundary
export const SMSFCalculatorInterface = ({ initialData = {}, onDataChange }) => {
    return (
        <SMSFErrorBoundary componentName="SMSF Calculator">
            <SMSFStateProvider initialData={initialData}>
                <SMSFCalculatorInterfaceInternal onDataChange={onDataChange} />
            </SMSFStateProvider>
        </SMSFErrorBoundary>
    );
};

// Export as default for backward compatibility
export default SMSFCalculatorInterface;
